import MasterDetail, { DetailAside } from '../ui/MasterDetail';
import { WorkspaceSurfaceContext } from '../ui/WorkspaceSurfaceContext';
import type { ReactNode } from 'react';
import PatientArrivalHistory from './PatientArrivalHistory';
import WalkinFollowupsPanel from '@/walkin/WalkinFollowupsPanel';
import SavedBookingDetails from '../appointments/SavedBookingDetails';
import AppointmentHistory, {appointmentDateTime} from '../appointments/AppointmentHistory';
import AcLink from '../ui/AcLink';
import SegmentedControl from '../ui/SegmentedControl';
import DataTable from '../ui/DataTable';
import { fetchPatientOperationalTreatments } from '../api';
/**
 * PATIENT-360-AND-EHNOTE-COEXISTENCE-SPINE-V1 (V3-P5
 * PATIENT-360-SPINE-CONNECT-V1) -- the Patient 360 record experience
 * (`/dashboard/patients/:patientId`).
 *
 * The EXISTING Patient Detail surface, extended in place (never a second
 * route, never a second patient owner): the identity header (the EXISTING
 * masked record read, PR #264 untouched) + the OPERATIONAL summary read
 * (GET .../summary360) composed into the 360 law shape --
 *
 *   1. identity header first (name, AdoraCare patient number, aliases
 *      under ONE identity, masked contact with the audited reveal),
 *   2. TODAY before history (the receptionist's 10-second NOW),
 *   3. exactly ONE visually dominant state-aware primary action
 *      (render-only orchestration: links into existing lawful flows),
 *   4. the honest journey strip (unproven segments never render complete),
 *   5. recent visit evidence, payment evidence + THE account line (the
 *      derivable plan remainder; PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 1
 *      superseded the never-a-balance posture for the PRESENTATION layer --
 *      the WIRE still carries payments.balance = 'not_available'),
 *      follow-up evidence, and the truthful EHNote coexistence state.
 *
 * GRACEFUL DEGRADATION: a failed summary read keeps the record page fully
 * usable (the record sections render; the operational sections state
 * "Operational summary unavailable") -- Patient 360 never breaks because
 * one evidence lane is dark (package section 2/10).
 *
 * Contact points stay MASKED BY DEFAULT: the wire carries no digits; each
 * point renders the CANONICAL audited reveal control (60s auto-rehide;
 * refresh/remount returns to masked). No raw phone exists in this page's
 * initial rendered state.
 */

import { recordPatientOpened } from '@/dashboard/patients/patient-recently-viewed';
import {
  clinicLabelOf,
  clinicOptionLabel,
  useClinicLens,
} from '@/dashboard/clinic-context';
import {
  fetchBillingQueue,
  fetchPatientBill,
  fetchPatientBills,
  fetchPatientReceipts,
} from '@/dashboard/api';
import BillPrintView from '@/dashboard/billing/BillPrintView';
import ReceiptPrintView from '@/dashboard/billing/ReceiptPrintView';
import {
  BILL_STATUS_LABELS,
  readPatientBillListWire,
  type PatientBillWireMirror,
} from '@/dashboard/billing/bill-view';
import {
  episodeStateLabel,
  formatMinorUnitsInr,
  type BillingQueueRow,
  type BillingQueueResponse,
  type PatientPaymentRow,
} from '@/dashboard/billing/billing-entry-view';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  fetchPatient360Summary,
  fetchPatientMasterRecord,
  revealPatientContact,
} from '@/dashboard/api';
import LeadContactRevealControl from '@/dashboard/owner/LeadContactReveal';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { formatClinicLocalDate } from '@/lib/age';
import { useDashboardMeContext } from '@/dashboard/me-context';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { portalErrorStatus, safeErrorMessage } from '@/lib/portal-api';
import {
  AcBadge,
  AcButton,
  DrawerShell,
  EmptyState,
  ErrorState,
  FieldGrid,
  HighlightsPanel,
  JourneyStageLadder,
  KpiTile,
  LoadingState,
  PrintDoor,
  RecordHeader,
  SectionContainer,
  SectionBand,
  SectionShell,
  acButtonClass,
  type AcBadgeTone,
  type FieldGridField,
  type HighlightFact,
  type RoleLensAudience,
} from '@/dashboard/ui';
import { formatIstDateTime, formatIstDate } from '@/lib/datetime-ist';
import { deriveCountsStageLadder } from '@/dashboard/doctor/consult-stage-view';
import {
  CappedHistoryFooter,
  PatientActivityLane,
  PatientAppointmentsTable,
  PatientCourseMoneyTable,
  PatientPaymentsTable,
  RecordDetailList,
  RegionSkeleton,
} from '@/dashboard/patients/Patient360Regions';
import {
  DESK_RECORD_DEFAULT_DOOR,
  contactPointAvailability,
  contactPointTypeLabel,
  deskRecordBackDoor,
  genderAgeLabel,
  parseDeskRecordOrigin,
  patientRecordHref,
  type PatientContactRevealResult,
  type PatientRecordResponse,
} from '@/dashboard/patients/patient-master-view';
import type { OriginDoor } from '@/dashboard/return-to-origin';
import {
  ACTION_REASON_COPY,
  appointmentDoctorQueueLabel,
  buildCourseMoneyLines,
  buildPatientHighlights,
  buildPatientNextAction,
  capInlineHistory,
  clampJourneyLadderMonotonic,
  consentStatusView,
  fullHistoryDoorLabel,
  inlineHistoryHeaderLine,
  selectKeyFacts,
  PAYMENT_HISTORY_INLINE_CAP,
  VISIT_HISTORY_INLINE_CAP,
  consultAttributionLabel,
  consultAttributionPending,
  deskActionLabel,
  CLINICAL_STATE_LABELS,
  buildPatientActivity,
  patientSummaryVisits,
  EXTERNAL_PROVENANCE_COPY,
  EXTERNAL_RECORD_COPY,
  EXTERNAL_STATUS_COPY,
  FOLLOW_UP_STATE_LABELS,
  JOURNEY_SEGMENT_LABELS,
  JOURNEY_STATE_LABELS,
  NO_APPOINTMENT_TODAY_COPY,
  NO_EXTERNAL_MR_COPY,
  PAYMENT_BALANCE_COPY,
  PAYMENT_HISTORY_SUB_COPY,
  PAYMENT_HISTORY_UNAVAILABLE_COPY,
  PAYMENT_STATE_LABELS,
  PAYMENT_VOIDED_STATUS_LABEL,
  TREATMENT_DISCOUNT_STATE_LABELS,
  JOURNEY_COMPLETION_UNAVAILABLE_COPY,
  JOURNEY_NO_COURSES_COPY,
  JOURNEY_UNAVAILABLE_COPY,
  TREATMENT_NO_ITEMS_COPY,
  TREATMENT_PRICE_UNAVAILABLE_COPY,
  TREATMENT_SUMMARY_UNAVAILABLE_COPY,
  appointmentStatusLabel,
  classifyPatientAliases,
  conversionStateLabel,
  formatPaymentAmount,
  intakeEvidenceLabel,
  paymentMethodLabel,
  paymentVoidReasonLabel,
  primaryActionView,
  waitingMinutes,
  consentTypeLabel,
  CONSENTS_NONE_COPY,
  NEXT_VISIT_NONE_COPY,
  nextVisitIntentLine,
  type Patient360AppointmentWire,
  type Patient360SummaryResponse,
  type Patient360VoidedPaymentItemWire,
  type PatientCourseMoneyLine,
  type PatientJourneySegmentState,
  VISIT_CLINIC_LABEL,
} from '@/dashboard/patients/patient-360-view';

export const OPERATIONAL_SUMMARY_UNAVAILABLE_COPY =
  'Operational summary unavailable - the record below remains current.';

// --- R-Q04: the read-only Patient 360 financial view -------------------------

export const PATIENT_MONEY_SECTION_TITLE = 'Money - this patient';
export const PATIENT_MONEY_EMPTY_COPY =
  'No open episode balance. Payment evidence stays in the payments section.';
export const PATIENT_MONEY_UNAVAILABLE_COPY =
  'Episode balances unavailable on this read.';
export const PATIENT_MONEY_LAST_PAYMENT_LABEL = 'Last payment';

/** The print door's label on a visit row. ONE place, so the door reads the
 *  same wherever the desk meets one. */
export const PATIENT_MONEY_PRINT_BILL_LABEL = 'Print bill';

/** The per-episode outstanding TABLE, extracted from PatientMoneySection so
 *  the rows (and the FRONT-DESK-MONEY-AND-DOCUMENTS-V1 print door on them)
 *  are renderable without the section's fetch. Pure: it derives nothing,
 *  fetches nothing and writes nothing -- the SAME cells, moved. */
export function PatientMoneyRowsTable({
  rows,
  onPrintBill = null,
}: {
  readonly rows: readonly BillingQueueRow[];
  /** null = the host mounted NO door: no Document column renders at all, so
   *  the pre-package table shape is byte-identical for every caller that
   *  passes none (OMISSION IS STRUCTURAL, never a disabled control). */
  readonly onPrintBill?:
    | ((row: { readonly appointmentId: number; readonly clinicId: number }) => void)
    | null;
}): JSX.Element {
  return (
    <table className="table ac-table" data-testid="patient-money-rows">
      <thead>
        <tr>
          <th>Visit</th>
          <th>State</th>
          <th>Outstanding</th>
          {onPrintBill !== null ? <th>Document</th> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.appointmentId}
            data-testid={`patient-money-row-${r.appointmentId}`}
          >
            <td>Appointment #{r.appointmentId}</td>
            <td>{episodeStateLabel(r.state)}</td>
            <td>
              {r.outstandingMinorUnits !== null
                ? formatMinorUnitsInr(r.outstandingMinorUnits)
                : '-'}
            </td>
            {onPrintBill !== null ? (
              <td>
                <PrintDoor
                  label={PATIENT_MONEY_PRINT_BILL_LABEL}
                  testId={`patient-money-print-bill-${r.appointmentId}`}
                  onOpen={() =>
                    onPrintBill({
                      appointmentId: r.appointmentId,
                      clinicId: r.clinicId,
                    })
                  }
                />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The patient's BILL list, extracted from PatientDocumentsSection so the rows
 *  and their print doors are renderable without the section's fetch -- the
 *  PatientMoneyRowsTable idiom, verbatim. Pure: derives nothing, fetches
 *  nothing, writes nothing. Every number is the server's own derivation. */
export function PatientBillsTable({
  bills,
  onPrintBill,
}: {
  readonly bills: readonly PatientBillWireMirror[];
  readonly onPrintBill: (row: {
    readonly billId: number;
    readonly clinicId: number;
  }) => void;
}): JSX.Element {
  return (
    <div className="ac-table-scroll">
      <table className="table ac-table" data-testid="patient-documents-bills">
        <thead>
          <tr>
            <th>Bill</th>
            <th>Visit</th>
            <th>Raised</th>
            <th>Status</th>
            <th className="ac-table__cell--end">Net</th>
            <th>Document</th>
          </tr>
        </thead>
        <tbody>
          {bills.map((b) => (
            <tr key={b.id} data-testid={`patient-documents-bill-${b.id}`}>
              <td>#{b.id}</td>
              <td>Appointment #{b.appointmentId}</td>
              <td>{formatIstDateTime(b.createdAt)}</td>
              {/* A CANCELLED bill stays in the list, named -- never deleted,
                  never hidden (S6 / the writer's own append-only posture). */}
              <td data-testid={`patient-documents-bill-status-${b.id}`}>
                {BILL_STATUS_LABELS[b.status]}
              </td>
              <td className="ac-table__cell--end">
                {formatMinorUnitsInr(b.totals.netMinorUnits)}
              </td>
              <td>
                <PrintDoor
                  label={PATIENT_DOCUMENTS_REPRINT_LABEL}
                  testId={`patient-documents-print-bill-${b.id}`}
                  onOpen={() =>
                    onPrintBill({ billId: b.id, clinicId: b.clinicId })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The patient's RECEIPT list, extracted for the same reason. Each row is one
 *  committed patient_payments row; the sheet its door opens renders THAT row,
 *  so a re-print carries the issue-day numbers by construction. */
export function PatientReceiptsTable({
  receipts,
  onPrintReceipt,
}: {
  readonly receipts: readonly PatientPaymentRow[];
  readonly onPrintReceipt: (payment: PatientPaymentRow) => void;
}): JSX.Element {
  return (
    <div className="ac-table-scroll">
      <table className="table ac-table" data-testid="patient-documents-receipts">
        <thead>
          <tr>
            <th>Receipt</th>
            <th>Received</th>
            <th className="ac-table__cell--end">Amount</th>
            <th>Method</th>
            <th>Reference</th>
            <th>Status</th>
            <th>Document</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((p) => {
            const voided = p.voidedAt !== null;
            return (
              <tr key={p.id} data-testid={`patient-documents-receipt-${p.id}`}>
                <td>#{p.id}</td>
                <td>{formatIstDateTime(p.occurredAt)}</td>
                <td className="ac-table__cell--end">
                  {voided ? (
                    <s>{formatMinorUnitsInr(p.amountMinorUnits)}</s>
                  ) : (
                    formatMinorUnitsInr(p.amountMinorUnits)
                  )}
                </td>
                <td>{paymentMethodLabel(p.method)}</td>
                {/* L-03's token, on the row the desk reconciles from. */}
                <td data-testid={`patient-documents-receipt-ref-${p.id}`}>
                  {p.referenceNumber !== null && p.referenceNumber !== undefined
                    ? p.referenceNumber
                    : '-'}
                </td>
                {/* A VOIDED payment stays listed and stays printable -- its
                    sheet prints "Voided", so a re-print can never pass a
                    reversed payment off as live. */}
                <td data-testid={`patient-documents-receipt-status-${p.id}`}>
                  {voided ? 'Voided' : 'Recorded'}
                </td>
                <td>
                  <PrintDoor
                    label={PATIENT_DOCUMENTS_REPRINT_LABEL}
                    testId={`patient-documents-print-receipt-${p.id}`}
                    onOpen={() => onPrintReceipt(p)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** L-17(b) copy, one home each so a test and a frame quote the same string. */
export const PATIENT_DOCUMENTS_SECTION_TITLE = 'Bills and receipts';
export const PATIENT_DOCUMENTS_BILLS_EMPTY_COPY =
  'No bill has been raised for this patient yet.';
export const PATIENT_DOCUMENTS_RECEIPTS_EMPTY_COPY =
  'No payment has been recorded for this patient yet.';
/** The read did not answer. NEVER shares a heading with the empty states
 *  above: "nothing was billed" and "we do not know what was billed" send the
 *  desk to different actions (the door's own two-headings law). */
export const PATIENT_DOCUMENTS_BILLS_UNAVAILABLE_COPY =
  'The bill list could not be read, so this patient\'s bills are unknown here. Try again, or open Billing entry.';
export const PATIENT_DOCUMENTS_RECEIPTS_UNAVAILABLE_COPY =
  'The receipt list could not be read, so this patient\'s receipts are unknown here. Try again, or open Billing entry.';
export const PATIENT_DOCUMENTS_REPRINT_LABEL = 'Print';
/** B1: the older-page control. One home so a test and a frame quote it. */
export const PATIENT_DOCUMENTS_OLDER_LABEL = 'Show older';
/** B1: how many documents one page holds. Well inside the server's max of
 *  50, so a page is never silently truncated by the cap. */
export const PATIENT_DOCUMENTS_PAGE_SIZE = 20;
/** B6 / A2: each lane says WHAT IT COUNTS, so the two can never be read as
 *  contradicting. "Last payment" is the newest ACTIVE payment (a voided one
 *  is not a payment); the receipts list is the DOCUMENT history and shows
 *  voided rows too, marked. Different questions, both true, both labelled. */
export const PATIENT_DOCUMENTS_RECEIPTS_SCOPE_NOTE =
  'Every receipt issued, including cancelled ones. A cancelled receipt is marked and still prints.';

/**
 * L-17(b) -- PREVIOUS BILLING AND RECEIPT RETRIEVAL, at the patient grain.
 *
 * THE DEFECT THIS CLOSES: the bill door on the money rows above reaches only
 * the NEWEST bill of an episode that carries an accepted plan item, and a
 * receipt could be printed ONLY while it was still in the recording session
 * on Billing entry (BillingEntryPage's in-session receipt). A patient asking
 * tomorrow for yesterday's receipt could not be served at all.
 *
 * WHAT IT IS NOT: no new money model, no account, no total. This section
 * lists DOCUMENTS -- each row is one already-committed bill or one
 * already-committed payment -- and it computes nothing. The bill's numbers
 * are the server's derivation (deriveBillTotals, lib/patient-bills.ts) and
 * the receipt's are the committed row's own, so a re-print carries the same
 * numbers as the day it was issued BY CONSTRUCTION rather than by arithmetic.
 *
 * TWO INDEPENDENT READS, TWO INDEPENDENT LANES: a failure on one never blanks
 * the other, and neither ever blanks the page (the record's graceful-
 * degradation posture, verbatim).
 */
export function PatientDocumentsSection({
  patientId,
  onPrintBill,
  onPrintReceipt,
}: {
  readonly patientId: string;
  readonly onPrintBill: (row: {
    readonly billId: number;
    readonly clinicId: number;
  }) => void;
  /** B3: the host re-reads the selected receipt BY ITS ID before printing, so
   *  the sheet carries the authoritative status rather than the one the list
   *  happened to load with. */
  readonly onPrintReceipt: (payment: PatientPaymentRow) => void;
}): JSX.Element {
  const [bills, setBills] = useState<
    readonly PatientBillWireMirror[] | null
  >(null);
  const [billsFailed, setBillsFailed] = useState(false);
  const [billsMore, setBillsMore] = useState(false);
  const [receipts, setReceipts] = useState<readonly PatientPaymentRow[] | null>(
    null,
  );
  const [receiptsFailed, setReceiptsFailed] = useState(false);
  const [receiptsMore, setReceiptsMore] = useState(false);
  // CORRECTIVE 2 / B1: a generation token per lane. A patient change (or a
  // second Show older press) supersedes an in-flight page, and a superseded
  // response is DROPPED -- it must never append rows belonging to another
  // patient, or the same page twice.
  const gen = useRef({ bills: 0, receipts: 0 });

  /**
   * CORRECTIVE 2 / B1 -- WHY THIS IS AN OFFSET WALK AND NOT A GROWING LIMIT.
   *
   * The first fix raised the LIMIT on each press (20, 40, 60) and left the
   * offset at zero. The server caps a page at 50
   * (RECENT_LIMIT_MAX / BILL_LIST_LIMIT_MAX), so the third press asked for 60,
   * got 50, and rows 51+ were UNREACHABLE AT ANY PRESS -- while `hasMore`
   * stayed true forever, so the control never retired either. The browser walk
   * passed only because its mock did not enforce the server's cap.
   *
   * So: every request is ONE page of PATIENT_DOCUMENTS_PAGE_SIZE at
   * offset = rows already held. The page size is well under the server cap, so
   * no request is ever clamped, and the walk terminates exactly when the
   * server says there is no more.
   */
  const loadBills = useCallback(
    (offset: number) => {
      const token = (gen.current.bills += 1);
      if (offset === 0) {
        setBills(null);
        setBillsFailed(false);
        setBillsMore(false);
      }
      fetchPatientBills({
        patientId,
        offset,
        limit: PATIENT_DOCUMENTS_PAGE_SIZE,
      })
        .then((res) => {
          if (gen.current.bills !== token) return;
          // The TOLERANT boundary, reused: a serving side without the bill
          // contract degrades to the honest unavailable line, never a crash.
          const list = readPatientBillListWire(res);
          if (list.kind !== 'ready') {
            setBillsFailed(true);
            return;
          }
          setBills((held) =>
            offset === 0 ? list.bills : [...(held ?? []), ...list.bills],
          );
          setBillsMore(res.hasMore === true);
        })
        .catch(() => {
          if (gen.current.bills !== token) return;
          setBillsFailed(true);
        });
    },
    [patientId],
  );

  const loadReceipts = useCallback(
    (offset: number) => {
      const token = (gen.current.receipts += 1);
      if (offset === 0) {
        setReceipts(null);
        setReceiptsFailed(false);
        setReceiptsMore(false);
      }
      fetchPatientReceipts({
        patientId,
        offset,
        limit: PATIENT_DOCUMENTS_PAGE_SIZE,
      })
        .then((res) => {
          if (gen.current.receipts !== token) return;
          setReceipts((held) =>
            offset === 0 ? res.payments : [...(held ?? []), ...res.payments],
          );
          setReceiptsMore(res.hasMore === true);
        })
        .catch(() => {
          if (gen.current.receipts !== token) return;
          setReceiptsFailed(true);
        });
    },
    [patientId],
  );

  // A NEW PATIENT RESTARTS BOTH WALKS AT THE FIRST PAGE. Landing on another
  // record must never inherit rows, or a depth, from the previous one.
  useEffect(() => {
    loadBills(0);
    loadReceipts(0);
  }, [loadBills, loadReceipts]);

  return (
    <SectionShell
      eyebrow="Documents"
      title={PATIENT_DOCUMENTS_SECTION_TITLE}
      testId="patient-documents-section"
    >
      {/* THE RATIFIED LABEL, not a local restyle: ac-section__eyebrow is the
          registered small-caps treatment (SectionShell's own), and .ac-section
          is already a flex column with its own gap -- so each block is one
          plain child and this section adds NO utility class and NO margin.
          (visual-contract-guard RULE 3 caught the first attempt at exactly
          this, which reached for raw Tailwind: a missing primitive is added
          to the shared set, never styled around locally.) */}
      <div>
        <p className="ac-section__eyebrow">Bills</p>
        {billsFailed ? (
          <p data-testid="patient-documents-bills-unavailable">
            {PATIENT_DOCUMENTS_BILLS_UNAVAILABLE_COPY}
          </p>
        ) : bills === null ? (
          <LoadingState label="Loading bills..." lines={2} />
        ) : bills.length === 0 ? (
          <p data-testid="patient-documents-bills-empty">
            {PATIENT_DOCUMENTS_BILLS_EMPTY_COPY}
          </p>
        ) : (
          <>
            <PatientBillsTable bills={bills} onPrintBill={onPrintBill} />
            {/* B1: OLDER documents are reachable. The control is ABSENT when
                the server says there are none -- never a disabled button that
                teaches the desk the list might be lying. */}
            {billsMore ? (
              <AcButton
                variant="secondary"
                size="sm"
                onClick={() => loadBills(bills.length)}
                data-testid="patient-documents-bills-older"
              >
                {PATIENT_DOCUMENTS_OLDER_LABEL}
              </AcButton>
            ) : null}
          </>
        )}
      </div>

      <div>
        <p className="ac-section__eyebrow">Receipts</p>
        {/* B6 / A2: this lane says what it counts, so it cannot be read as
            contradicting the money block's "Last payment" (newest ACTIVE). */}
        <p data-testid="patient-documents-receipts-scope">
          {PATIENT_DOCUMENTS_RECEIPTS_SCOPE_NOTE}
        </p>
        {receiptsFailed ? (
          <p data-testid="patient-documents-receipts-unavailable">
            {PATIENT_DOCUMENTS_RECEIPTS_UNAVAILABLE_COPY}
          </p>
        ) : receipts === null ? (
          <LoadingState label="Loading receipts..." lines={2} />
        ) : receipts.length === 0 ? (
          <p data-testid="patient-documents-receipts-empty">
            {PATIENT_DOCUMENTS_RECEIPTS_EMPTY_COPY}
          </p>
        ) : (
          <>
            <PatientReceiptsTable
              receipts={receipts}
              onPrintReceipt={onPrintReceipt}
            />
            {receiptsMore ? (
              <AcButton
                variant="secondary"
                size="sm"
                onClick={() => loadReceipts(receipts.length)}
                data-testid="patient-documents-receipts-older"
              >
                {PATIENT_DOCUMENTS_OLDER_LABEL}
              </AcButton>
            ) : null}
          </>
        )}
      </div>
    </SectionShell>
  );
}

/**
 * RECEPTION-OS-BUILD-V1 (founder ruling Q-04 + R-Q04, 2026-08-03): the
 * ONE-CLICK read-only financial view -- the patient's PER-EPISODE
 * outstanding LIST (the SAME server-side netting the billing queue runs;
 * a patientId narrowing, never a second derivation) plus the LAST payment.
 * DELIBERATELY ABSENT BY RULING: computed account totals, advance/credit,
 * next-due -- those wait for the account model (gap.patient-account-model).
 * Read-only: no action, no writer, no navigation-forcing.
 */
export function PatientMoneySection({
  patientId,
  lastPayment,
  courseMoney = null,
  onPrintBill = null,
  summaryOnly = false,
}: {
  readonly summaryOnly?: boolean;
  readonly patientId: string;
  /** FRONT-DESK-MONEY-AND-DOCUMENTS-V1 (PR2, LEAVES-2 / L-17): the page
   *  host's print-door handler. THE HOST owns the swap (a print sheet is
   *  chrome-free only when it replaces the PAGE, the #442 idiom this repo
   *  already uses on BillingEntryPage), so this section only OFFERS the
   *  door and never renders a document. null = the host mounted no door,
   *  and no column appears -- the section is unchanged for every existing
   *  caller and every existing test. */
  readonly onPrintBill?:
    | ((row: { readonly appointmentId: number; readonly clinicId: number }) => void)
    | null;
  /** The newest ACTIVE payment from the record's own evidence lane
   *  (caller-resolved; null = none recorded). */
  readonly lastPayment: {
    readonly amountLabel: string;
    readonly occurredAt: string;
  } | null;
  /** PATIENT-360-V1 (B-2 fold, founder wording/label scope 2026-08-16):
   *  the course-grain money RELOCATED into this ONE money region, so the two
   *  money grains sit side by side, each named -- the account line no longer
   *  renders in Payment evidence.
   *
   *  PATIENT-360-DESIGN-CONSISTENCY-V1 (P2, founder MATCH 2026-08-17): the
   *  three identically-labeled "Plan remainder (course)" TILES are retired
   *  for ONE LINE PER COURSE -- each line naming its own course, its own due
   *  state and its own collected figure, with the across-all-courses roll-up
   *  beneath. Same wire, same owner, same numbers; the ambiguity is what
   *  changed. */
  readonly courseMoney?: readonly PatientCourseMoneyLine[] | null;
}): JSX.Element {
  const [rows, setRows] = useState<readonly BillingQueueRow[] | null>(null);
  // RECEPTION-MONEY-TRUTH-V1 (A2): the patient-grain summary from the SAME
  // patient-narrowed read (no second fetch) -- THE ONE owner's wire block.
  const [patientMoney, setPatientMoney] = useState<
    BillingQueueResponse['patientMoney'] | null
  >(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setPatientMoney(null);
    setFailed(false);
    fetchBillingQueue({ patientId })
      .then((res) => {
        if (!cancelled) {
          setRows(res.rows);
          setPatientMoney(res.patientMoney ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // B-1 fix (PATIENT-360-V1; the LES-126 raw-value class): the last-payment
  // timestamp renders through THE ONE IST owner, never the raw ISO.
  const lastPaymentWhen =
    lastPayment !== null
      ? (() => {
          const ist = formatIstDateTime(lastPayment.occurredAt);
          return ist === '' ? lastPayment.occurredAt : `${ist} IST`;
        })()
      : null;

  return (
    // PATIENT-360-V1 (founder ruling D-360f, 2026-08-16): the money truth is
    // an OPEN section for the money audience -- the #565 collapsed details
    // retired (the R-Q04 friction closed). Same reads, same testids, same
    // wording; the grains render side by side, each named (the B-2 fold).
    <SectionShell
      eyebrow="Money"
      title={PATIENT_MONEY_SECTION_TITLE}
      testId="patient-money-section"
    >
      {failed ? (
        <p data-testid="patient-money-unavailable">
          {PATIENT_MONEY_UNAVAILABLE_COPY}
        </p>
      ) : rows === null ? (
        <p>Loading...</p>
      ) : (
        <>
          {/* A2: the patient-grain money truth -- owed across visits /
              carried / paid till date / collected today -- now LABELED CELLS
              (one grain per cell, the D-360f promotion). Renders ONLY when
              the wire carried the block (honest omission on degraded reads);
              the course-grain remainder sits BESIDE it, named (B-2). */}
          {patientMoney !== null && patientMoney !== undefined ? (
            <div className="kpi-grid" data-testid="patient-money-summary">
              <KpiTile
                label="Owed across visits"
                value={
                  patientMoney.owedMinorUnits > 0
                    ? formatMinorUnitsInr(patientMoney.owedMinorUnits)
                    : 'Nothing outstanding'
                }
                sub={
                  [
                    patientMoney.carriedMinorUnits > 0
                      ? `carried from earlier visits ${formatMinorUnitsInr(patientMoney.carriedMinorUnits)}`
                      : null,
                    patientMoney.owedNotConfiguredCount > 0
                      ? `+${patientMoney.owedNotConfiguredCount} visit${
                          patientMoney.owedNotConfiguredCount === 1 ? '' : 's'
                        } without a configured amount`
                      : null,
                  ]
                    .filter((s): s is string => s !== null)
                    .join(' - ') || undefined
                }
                testId="patient-money-owed"
              />
              <KpiTile
                label="Paid till date"
                value={
                  patientMoney.paidToDateMinorUnits > 0
                    ? formatMinorUnitsInr(patientMoney.paidToDateMinorUnits)
                    : 'None recorded'
                }
                testId="patient-money-paid"
              />
              {patientMoney.collectedTodayMinorUnits > 0 ? (
                <KpiTile
                  label="Collected today"
                  value={formatMinorUnitsInr(patientMoney.collectedTodayMinorUnits)}
                  sub="this clinic day"
                  testId="patient-money-collected-today"
                />
              ) : null}
            </div>
          ) : summaryOnly ? (
            <p data-testid="patient-money-unavailable">{PATIENT_MONEY_UNAVAILABLE_COPY}</p>
          ) : null}
          <div hidden={summaryOnly}>
          {/* P2 -- COURSE-LEVEL MONEY: one line per treatment course, then
              the honest across-all-courses roll-up. The course grain sits
              beside the visit grain above, each named (the B-2 fold kept). */}
          {courseMoney !== null && courseMoney.length > 0 ? (
            <div className="mt-3" data-testid="patient360-course-money">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Money by treatment course
              </p>
              <PatientCourseMoneyTable
                lines={courseMoney}
                testId="patient360-course-money-table"
              />
            </div>
          ) : null}
          {rows.length === 0 ? (
            <p data-testid="patient-money-empty">{PATIENT_MONEY_EMPTY_COPY}</p>
          ) : (
            <PatientMoneyRowsTable rows={rows} onPrintBill={onPrintBill} />
          )}
          <p data-testid="patient-money-last-payment">
            {PATIENT_MONEY_LAST_PAYMENT_LABEL}:{' '}
            {lastPayment !== null && lastPaymentWhen !== null
              ? `${lastPayment.amountLabel} (${lastPaymentWhen})`
              : 'None recorded'}
          </p>
          </div>
        </>
      )}
    </SectionShell>
  );
}

const STATUS_TONE: Readonly<Record<string, AcBadgeTone>> = {
  booked: 'info',
  confirmed: 'info',
  rescheduled: 'info',
  waitlist: 'neutral',
  arrived: 'brand',
  checked_in: 'brand',
  fulfilled: 'positive',
  completed: 'positive',
  cancelled: 'danger',
  no_show: 'danger',
};

const JOURNEY_TONE: Readonly<Record<PatientJourneySegmentState, AcBadgeTone>> = {
  complete: 'positive',
  current: 'brand',
  pending: 'neutral',
  not_evidenced: 'pending',
  evidenced: 'info',
  unavailable: 'neutral',
  not_applicable: 'neutral',
};

type RecordWorkspace = 'overview' | 'related' | 'details';

export interface PatientRecordContentProps {
  readonly workspace?: RecordWorkspace;
  readonly onWorkspaceChange?: (workspace: RecordWorkspace) => void;
  readonly loading: boolean;
  readonly error: string | null;
  /** ERROR-STATUS-PASSTHROUGH-V1: the caught error's HTTP status, when
   *  known -- forwarded to DashboardLoadError so its own 404/401/403
   *  classification renders instead of the generic verbatim-message
   *  fallback. Optional so the pre-existing message-only render contract
   *  stays valid unchanged. */
  readonly errorStatus?: number;
  readonly record: PatientRecordResponse | null;
  /** The operational summary read; null with summaryError = the honest
   * degraded state (the record sections stay fully usable). Optional so
   * the pre-existing record-only render contract (the PR #264 privacy
   * pins) stays valid unchanged. */
  readonly summary?: Patient360SummaryResponse | null;
  readonly summaryError?: string | null;
  /**
   * TRUE while the operational summary read is still in flight. Drives the
   * per-region DESIGNED SKELETONS (D4): a region that is still loading must
   * never render its empty state, because an empty state asserts "there is
   * nothing" -- a lie while the read is running. Purely a state flag over
   * the two EXISTING reads; this package adds no fetch.
   */
  readonly summaryLoading?: boolean;
  /** RECEPTION-OS-BUILD-V1 (R-Q04): the read-only Patient 360 financial
   *  view slot (the page host mounts PatientMoneySection into it; pure
   *  renders omit it -- byte-identical without it). */
  readonly operationalTreatments?: import('../types').OperationalTreatmentsResult | null;
  readonly moneySection?: React.ReactNode | ((summaryOnly: boolean) => React.ReactNode);
  /** L-17(b): the bills-and-receipts DOCUMENT slot, mounted by the page host
   *  (it owns the id and the print swap). Pure renders omit it, so every
   *  pre-package render of this component stays byte-identical. */
  readonly documentsSection?: React.ReactNode;
  readonly arrivalHistory?: React.ReactNode;
  /** L-18: the resolved Back door. Absent = the honest default door, which is
   *  the pre-package constant, so every existing pure render is unchanged. */
  readonly backDoor?: OriginDoor | null;
  /** Appointment opened from a Reception row; never inferred from today's focus. */
  readonly selectedAppointmentId?: number | null;
  /**
   * The viewer's role, from `me.role` (server truth). The role-shaped
   * primitives fail CLOSED on an unknown/absent value by their own contract.
   *
   * LAW 2 (PER-SURFACE-ROLE-PROOF): this is PRESENTATION shaping only and
   * never a read widener. The route already admits exactly the server's
   * PATIENT_MASTER_ROLES set (coc / gm_agm / owner / receptionist /
   * regional_head -- doctor, call_center_agent and patient are refused with
   * an AuthError by api/routes/patient-master-360.ts), and every one of those
   * five is a STAFF audience, so the staff-default facet fences in
   * RecordHeader / RelatedHistory admit all five and change nothing that was
   * visible before this package.
   */
  readonly audience?: RoleLensAudience | string | null;
  /** UX gating only (merge audience = me.patientMasterMergeEnabled);
   * the backend merge gate remains the boundary. */
  readonly canOpenMergeQueue?: boolean;
  /** Injected clock for deterministic waiting-duration rendering. */
  readonly nowMs?: number;
  /** Injected audited reveal (defaults to the real client; tests drive it
   * without a network round trip). Masked default render never calls it. */
  readonly revealContact?: (
    patientId: string,
    contactPointId: number,
    purpose: string,
  ) => Promise<PatientContactRevealResult>;
}

// --- TODAY panel ------------------------------------------------------------

/** F-2: the "Doctor queue" field now routes through the ONE shared contract
 *  (appointmentDoctorQueueLabel), so it can never contradict the "Doctor"
 *  field again -- the label itself states the assignment truth. */
function queueStateLabel(a: Patient360AppointmentWire): string {
  return appointmentDoctorQueueLabel(a);
}

function linkageBadge(a: Patient360AppointmentWire): JSX.Element {
  return a.linkage === 'direct' ? (
    <AcBadge tone="positive" testId="patient360-linkage-direct">
      Linked to this patient
    </AcBadge>
  ) : (
    <AcBadge
      tone="pending"
      testId="patient360-linkage-lead"
      title="Reached through the lead relationship; the appointment row itself is not directly linked (historical backfill pending)"
    >
      Via lead - not directly linked
    </AcBadge>
  );
}

function TodayAppointmentCard({
  appointment,
  nowMs,
  focus,
}: {
  appointment: Patient360AppointmentWire;
  nowMs: number;
  focus: boolean;
}): JSX.Element {
  const waited = waitingMinutes(appointment.arrivedAt, nowMs);
  const showWaiting =
    waited !== null &&
    (appointment.status === 'arrived' || appointment.status === 'checked_in') &&
    !appointment.consultStarted && appointment.clinicalState !== 'in_progress' &&
    appointment.clinicalState !== 'completed';
  // VISIT-ACTOR-ATTRIBUTION-V1: WHO ran the consult (or the honest
  // "Unattributed" line) -- shown only when a consult act occurred, resolving
  // the "Doctor: Not assigned" beside "Consult completed" contradiction (FND-14).
  const consultBy = consultAttributionLabel(appointment);
  const fields: FieldGridField[] = [
    {
      label: 'Status',
      value: (
        <AcBadge
          tone={STATUS_TONE[appointment.status] ?? 'neutral'}
          testId="patient360-today-status"
        >
          {appointmentStatusLabel(appointment.status)}
        </AcBadge>
      ),
    },
    {
      label: 'Scheduled (IST)',
      value: formatTimestampIst(appointment.scheduledAt),
    },
    { label: VISIT_CLINIC_LABEL, value: clinicLabelOf(appointment.clinicLabel) },
    {
      label: 'Doctor',
      value: appointment.doctorName,
      missingLabel: 'Not assigned',
      testId: 'patient360-today-doctor',
    },
    {
      label: 'Arrived (IST)',
      value:
        appointment.arrivedAt !== null
          ? formatTimestampIst(appointment.arrivedAt)
          : null,
      missingLabel: 'Not recorded',
    },
    ...(showWaiting
      ? [{ label: 'Waiting', value: `${waited} min` } as FieldGridField]
      : []),
    {
      label: 'Intake',
      value: (
        <span data-testid="patient360-today-intake">
          {intakeEvidenceLabel(appointment.intake)}
        </span>
      ),
    },
    {
      label: 'Checked in',
      value:
        appointment.status === 'checked_in' ||
        appointment.status === 'fulfilled' ||
        appointment.status === 'completed'
          ? 'Yes'
          : 'No',
    },
    { label: 'Doctor queue', value: queueStateLabel(appointment) },
    // FLAG 2 (copy only): an attributed consult keeps the banked
    // "Consult by <actor>" field; the no-actor state renders the NEUTRAL
    // pending fact instead of a judgement-shaped label. Same derivation,
    // same visibility, same testId.
    ...(consultBy !== null
      ? [
          consultAttributionPending(appointment)
            ? ({
                label: 'Doctor attribution',
                value: 'Pending',
                testId: 'patient360-consult-actor',
              } as FieldGridField)
            : ({
                label: 'Consult by',
                value: consultBy,
                testId: 'patient360-consult-actor',
              } as FieldGridField),
        ]
      : []),
    {
      label: 'Clinical',
      value: (
        <span data-testid="patient360-clinical-state">
          {CLINICAL_STATE_LABELS[appointment.clinicalState]}
        </span>
      ),
    },
    {
      label: 'Payment',
      value: appointment.paymentRecorded ? 'Payment recorded' : 'No payment recorded',
    },
    { label: 'Appointment link', value: linkageBadge(appointment) },
  ];
  return (
    <div
      className="rounded-md border border-border px-4 py-3"
      data-testid={focus ? 'patient360-today-focus' : 'patient360-today-other'}
    >
      <FieldGrid label="Today appointment facts" fields={fields} columns={2} />
    </div>
  );
}

// --- Voided payment history row (#273 / FND-01) ------------------------------

/** ONE voided payment on the history lane: the immutable corrected evidence,
 * marked VOIDED (danger chip; never counted as active collection). The void
 * REASON and the void ACTOR render ONLY when the wire carries them (the
 * supervisor tier -- billing-sensitive detail fenced server-side; absent =
 * `undefined`, so a non-supervisor simply never sees them). */
function VoidedPaymentRow({
  row,
}: {
  row: Patient360VoidedPaymentItemWire;
}): JSX.Element {
  return (
    <li
      className="ac-payment-history__row"
      data-testid="patient360-voided-payment-row"
      data-payment-id={row.paymentId}
    >
      <span
        className="ac-payment-history__amount"
        data-testid="patient360-voided-amount"
      >
        {formatPaymentAmount(row.amountMinorUnits, row.currency)}
      </span>
      <AcBadge tone="danger" testId="patient360-voided-status">
        {PAYMENT_VOIDED_STATUS_LABEL}
      </AcBadge>
      <span className="text-muted-foreground">{paymentMethodLabel(row.method)}</span>
      <span className="text-muted-foreground">
        Recorded {formatTimestampIst(row.occurredAt)}
      </span>
      {row.voidedAt !== null ? (
        <span className="text-muted-foreground">
          Voided {formatTimestampIst(row.voidedAt)}
        </span>
      ) : null}
      {row.voidReason !== undefined ? (
        <span data-testid="patient360-voided-reason">
          Reason: {paymentVoidReasonLabel(row.voidReason)}
        </span>
      ) : null}
      {row.voidedByLabel !== undefined && row.voidedByLabel !== null ? (
        <span data-testid="patient360-voided-actor">by {row.voidedByLabel}</span>
      ) : null}
    </li>
  );
}

// --- Pure presentational body (rendered directly in tests) -------------------

/** A standalone projection: shared summary never carries treatment names. */
export function OperationalTreatmentTable({ result, onVisit }: {
  result: import('../types').OperationalTreatmentsResult | null;
  onVisit: (visitId: number) => void;
}): JSX.Element {
  if (result === null) return <RegionSkeleton label="Loading treatments..." testId="operational-treatments-loading" />;
  if (result.availability === 'unavailable') return <ErrorState title="Treatment details are unavailable." />;
  if (!result.rows.length) return <EmptyState title="No treatments recorded for the available visits." />;
  const visitLabel = (id: number, date: string) => `${formatIstDate(date)} · #${id}`;
  return <>
    <DataTable label="Operational treatments" rows={result.rows} rowKey={r => r.itemId} testId="operational-treatments" columns={[
      { key: 'name', header: 'Treatment', render: r => <div className="ac-cell-stack">{r.treatmentName ?? 'Treatment name unavailable'}</div> },
      { key: 'decision', header: 'Decision', render: r => ({ accepted: 'Accepted', declined: 'Declined', deferred: 'Deferred', undecided: 'Undecided' })[r.decision] },
      { key: 'completion', header: 'Completion', render: r => r.decision === 'declined' && r.completion === 'not_recorded' ? '—' : ({ completed: 'Completed', not_recorded: 'No completion recorded', unavailable: 'Completion unavailable' })[r.completion] },
      { key: 'visit', header: 'Visit', render: r => <div className="ac-cell-stack">
        <AcLink onClick={() => onVisit(r.owningVisitId)}>{r.completingVisitId !== null ? 'Recorded: ' : ''}{visitLabel(r.owningVisitId, r.owningVisitDate)}</AcLink>
        {r.completingVisitId !== null && r.completingVisitDate !== null ? <AcLink onClick={() => onVisit(r.completingVisitId!)}>Completed: {visitLabel(r.completingVisitId, r.completingVisitDate)}</AcLink> : null}
      </div> },
    ]} />
    {result.truncated ? <p role="status">Showing a limited treatment history. More records may exist.</p> : null}
  </>;
}

export function PatientRecordContent({
  workspace: controlledWorkspace,
  onWorkspaceChange,
  loading,
  error,
  errorStatus = undefined,
  record,
  summary = null,
  summaryError = null,
  summaryLoading = false,
  operationalTreatments = null,
  moneySection = null,
  documentsSection = null,
  arrivalHistory = null,
  backDoor = DESK_RECORD_DEFAULT_DOOR,
  selectedAppointmentId = null,
  audience = null,
  canOpenMergeQueue = false,
  nowMs = 0,
  revealContact = revealPatientContact,
}: PatientRecordContentProps): JSX.Element {
  // P3 -- the ONE full-history drawer for this carrier. Declared before the
  // early returns (rules of hooks); closed by default, so a server render
  // and the landing frame are byte-identical to a page without it.
  const [localWorkspace, setLocalWorkspace] = useState<RecordWorkspace>('overview');
  const workspace = controlledWorkspace ?? localWorkspace;
  const setWorkspace = (next: RecordWorkspace): void => {
    setLocalWorkspace(next);
    onWorkspaceChange?.(next);
  };
  const visitDetailsRef = useRef<HTMLDivElement>(null);
  const selectedVisitRef = useRef<HTMLDivElement>(null);
  const [selectedOperationalVisit, setSelectedOperationalVisit] = useState<number | null>(null);
  useEffect(() => {
    if (selectedOperationalVisit !== null) {
      setWorkspace('overview');
      visitDetailsRef.current?.querySelector('details')?.setAttribute('open', '');
      selectedVisitRef.current?.focus();
    }
  }, [selectedOperationalVisit]);
  const [fullHistory, setFullHistory] = useState<'visits' | 'payments' | null>(
    null,
  );
  if (loading) {
    return (
      <LoadingState
        label="Loading patient..."
        lines={3}
        testId="patient360-loading"
      />
    );
  }
  if (error) {
    // Structured, recoverable error (the shared DashboardLoadError: honest
    // sign-in / access / not-available / generic states with a recovery path).
    return (
      <div data-testid="patient360-error">
        <DashboardLoadError
          message={error}
          {...(errorStatus !== undefined ? { status: errorStatus } : {})}
        />
      </div>
    );
  }
  if (!record) {
    return (
      <EmptyState
        testId="patient360-not-found"
        title="Patient not found."
        body="This record is not in your scope, or the id is unknown."
      />
    );
  }

  const { patient } = record;
  const aliases = classifyPatientAliases(record.identifiers);

  // Where the patient ENTERED THE NETWORK. Null id = no registration clinic
  // on the record, and the facet is omitted rather than guessed. A present id
  // with no label resolves through THE ONE labeller ("Unnamed clinic") -- a
  // raw `Clinic #<id>` on the glass is against the Phase 2 ruling.
  const registeredClinicLabel =
    patient.registeredClinicId !== null
      ? clinicLabelOf(patient.registeredClinicLabel)
      : null;

  // The next upcoming appointment -- hoisted into the header (D1) so the
  // decision-relevant fact sits above the fold. It renders EXACTLY ONCE on
  // the page; the visit-history region no longer repeats it.
  const nextAppointment = summary?.upcomingAppointments[0] ?? null;

  // The appointment rows that belong in the CURRENT related list: today's
  // non-focus rows plus every upcoming row. The FOCUS appointment keeps its
  // own detailed card (P10: card for the single object under full attention),
  // so no appointment is rendered twice.
  const focusAppointmentId = summary?.todayFocus?.appointmentId ?? null;
  const selectedAppointment = selectedAppointmentId === null || summary === null
    ? null
    : summary.selectedAppointment?.appointmentId === selectedAppointmentId
      ? summary.selectedAppointment
      : [...summary.todayAppointments, ...summary.upcomingAppointments, ...summary.recentVisits]
          .find(appointment => appointment.appointmentId === selectedAppointmentId) ?? null;
  const selectedVisitMissing = selectedAppointmentId !== null && summary !== null && selectedAppointment === null;
  // The summary journey and desk action describe its TODAY focus, not every
  // visit in the bounded appointment lists. Do not attach either to another
  // selected appointment merely because that appointment appears in a list.
  const selectedIsTodayFocus = selectedAppointmentId === null ||
    (summary?.todayFocus?.appointmentId === selectedAppointmentId);
  const currentAppointmentRows: readonly Patient360AppointmentWire[] =
    summary !== null
      ? [
          ...summary.todayAppointments.filter(
            (a) => a.appointmentId !== focusAppointmentId,
          ),
          ...summary.upcomingAppointments,
        ]
      : [];

  // The HISTORY side: past visits + the immutable voided-payment corrections,
  // merged newest-first by the pure builder. No new fetch, no new fact.
  const activityEntries = summary !== null ? buildPatientActivity(summary) : [];
  // Reuse the existing history order before capping; the server wire need not be sorted.
  const visitsByActivityId = new Map((summary ? patientSummaryVisits(summary) : []).map(visit => [`visit:${visit.appointmentId}`, visit]));
  const recentVisitRows = activityEntries.flatMap(entry => {
    const visit = visitsByActivityId.get(entry.id);
    return visit ? [visit] : [];
  }).slice(0, VISIT_HISTORY_INLINE_CAP);

  // FLAG 1 lineage (PATIENT-360-V1): the derivable remainder lines now feed
  // the MONEY block's course cells -- the page HOST derives them at the
  // moneySection mount, so the content component no longer holds a copy.

  // The header identity facts. `Registered at` moves onto the primitive's own
  // `clinicLabel` facet, so it is NOT repeated here.
  const headerFacts: FieldGridField[] = [
    {
      label: 'Patient number',
      value: (
        <span className="font-mono" data-testid="patient360-umr">
          {patient.umr}
        </span>
      ),
    },
    {
      label: 'Patient',
      value: genderAgeLabel(
        patient.gender,
        patient.ageYears,
        patient.ageIsApproximate,
      ),
    },
    ...(patient.status !== 'active'
      ? [
          {
            label: 'Record status',
            value: (
              <AcBadge tone="neutral" testId="patient360-record-status">
                {patient.status}
              </AcBadge>
            ),
          } as FieldGridField,
        ]
      : []),
    ...(nextAppointment !== null
      ? [
          {
            label: 'Next appointment',
            value: (
              <span data-testid="patient360-next-appointment">
                {formatTimestampIst(nextAppointment.scheduledAt)}
                {nextAppointment.clinicLabel !== null
                  ? ` at ${nextAppointment.clinicLabel}`
                  : ''}
              </span>
            ),
          } as FieldGridField,
        ]
      : []),
  ];

  const action =
    summary !== null
      ? primaryActionView(summary.primaryAction.action, {
          patientId: patient.patientId,
          focusClinicId: summary.todayFocus?.clinicId ?? null,
          focusAppointmentId: summary.todayFocus?.appointmentId ?? null,
          canOpenMergeQueue,
        })
      : null;
  const actionReason =
    summary !== null
      ? (ACTION_REASON_COPY[summary.primaryAction.reason] ?? null)
      : null;
  // F1 / #290: today's desk action and the PATIENT next action are DISTINCT
  // facts. `deskActionLabel` keeps the desk chip honest -- an idle desk on an
  // unfinished journey never reads "No action required" -- and the patient
  // next action (continuity + commercial) surfaces the outstanding work.
  const deskLabel =
    summary !== null && action !== null
      ? deskActionLabel(summary, action)
      : null;
  const patientNextAction =
    summary !== null
      ? buildPatientNextAction(summary, {
          patientId: patient.patientId,
          focusClinicId: summary.todayFocus?.clinicId ?? null,
          focusAppointmentId: summary.todayFocus?.appointmentId ?? null,
        })
      : null;

  // The five-second decision set (#278 / FND-17): the receptionist's scan-first
  // highlights, derived from the operational summary. Rendered above the
  // detailed sections so the flat all-cards-equal hierarchy gives way to a
  // clear "who/why/next/money" glance.
  // PATIENT-360-DESIGN-CONSISTENCY-V1 (founder MATCH 2026-08-17): the ruled
  // FOUR key facts. selectKeyFacts chooses what reads FIRST from the SAME
  // builder -- it invents nothing, and every fact it does not promote still
  // renders in full further down this page.
  const highlights: readonly HighlightFact[] =
    summary !== null
      ? selectKeyFacts(buildPatientHighlights(summary)).map((h) => ({
          key: h.key,
          label: h.label,
          value: h.value,
          tone: h.tone,
          emphasis: h.emphasis,
          testId: `patient360-highlight-${h.key}`,
        }))
      : [];

  // P3 -- the capped inline histories. The page's HEIGHT is now a function of
  // the CAP, never of how long this patient has been with the clinic.
  const activityCapped = capInlineHistory(
    activityEntries,
    VISIT_HISTORY_INLINE_CAP,
  );
  const paymentsCapped = capInlineHistory(
    summary?.payments.items ?? [],
    PAYMENT_HISTORY_INLINE_CAP,
  );
  // Contract 8a: the cap rides the HEADER. `recentVisitsCapped` means the
  // SERVER already windowed the history, so the denominator in hand is not
  // the patient's whole life at the clinic and must not be quoted as one.
  const activityHeaderLine = inlineHistoryHeaderLine(
    activityCapped,
    summary?.recentVisitsCapped === true,
  );

  // P5 -- consent severity. Missing consent is AMBER; making it red is a
  // policy act (Deepak/Partha), never a visual one.
  const consentStatus = consentStatusView(record.consents.length);

  const voidedPayments = summary?.paymentHistory?.voided ?? [];
  const paymentHistoryUnavailable =
    summary?.paymentHistory?.state === 'unavailable';

  return (
    <section aria-label="Patient record" className="patient-record-workspace" data-workspace={workspace} data-selected-appointment={selectedAppointmentId !== null ? "true" : undefined}>
      {backDoor !== null ? <AcLink to={backDoor.href} testId="patient-record-back">← {backDoor.label}</AcLink> : null}
      {patient.status === 'merged' && patient.mergedIntoPatientId !== null ? <div role="status">This record was merged. It is kept for the audit trail. <AcLink to={patientRecordHref(patient.mergedIntoPatientId)}>Open the surviving record</AcLink></div> : null}
      <div className="ac-section patient-record-workspace__header">
        <RecordHeader audience={audience} testId="patient360-header" name={patient.fullName} registeredClinicLabel={selectedAppointmentId === null ? registeredClinicLabel : null} facts={selectedAppointmentId === null ? headerFacts : [
            {label:'UMR',value:<span data-testid="patient360-umr">{patient.umr}</span>},
            {label:'Clinic',value:selectedAppointment ? clinicLabelOf(selectedAppointment.clinicLabel) : 'Not recorded'},
          ]}
          summary={selectedAppointment ? <SavedBookingDetails appointmentId={selectedAppointment.appointmentId} patientId={patient.patientId} clinicId={selectedAppointment.clinicId} summaryOnly /> : null}
          status={selectedAppointmentId !== null
            ? selectedAppointment !== null ? { domain: 'appointment', status: selectedAppointment.status } : null
            : summary?.todayFocus ? { domain: 'appointment', status: summary.todayFocus.status } : null}
          actions={selectedAppointment && !action?.href && !patientNextAction?.href ? null : <div className="grid gap-2" data-testid="patient360-primary-answer">
            {selectedVisitMissing ? <span role="status">Selected appointment unavailable</span> : !selectedIsTodayFocus ? <span role="status">Selected appointment details below</span> : <>
              <div data-testid="patient360-primary-action" aria-label="Today's desk action">{action?.href ? <AcLink testId="patient360-primary-action-link" className={acButtonClass('primary')} to={action.href} aria-label={"Today's desk action: " + action.label}>{action.label}</AcLink> : <span data-testid="patient360-primary-action-state">{deskLabel}</span>}</div>
              {patientNextAction !== null ? <div data-testid="patient360-patient-next-action">{patientNextAction.href ? <AcLink testId="patient360-patient-next-action-link" className={acButtonClass('primary')} to={patientNextAction.href} aria-label={"Patient next action: " + patientNextAction.label}>{patientNextAction.label}</AcLink> : <span>{patientNextAction.label}</span>}</div> : null}
            </>}
          </div>} />
      </div>
      {selectedAppointmentId !== null && summary !== null ? (
        <SectionShell title="Selected appointment" testId="patient360-selected-appointment">
          {selectedAppointment !== null ? <>
            <FieldGrid label="Selected appointment facts" columns={1} fields={[
              { label: 'Date and time', value: appointmentDateTime(selectedAppointment.scheduledAt) },
              { label: 'Doctor', value: selectedAppointment.doctorName ?? 'Not recorded' },
              { label: 'Status', value: appointmentStatusLabel(selectedAppointment.status) },
            ]} />
            <SectionBand title="Appointment details" collapsible><FieldGrid columns={1} fields={[
              {label:'Appointment',value:`#${selectedAppointment.appointmentId}`},
              {label:'Clinic',value:clinicLabelOf(selectedAppointment.clinicLabel)},
            ]} /><SavedBookingDetails appointmentId={selectedAppointment.appointmentId} patientId={patient.patientId} clinicId={selectedAppointment.clinicId} /></SectionBand>
            <AppointmentHistory key={selectedAppointment.appointmentId} history={summary.selectedAppointmentHistory} />
          </> : <p role="status">This appointment is not in the available patient summary. Reopen it from the day board.</p>}
        </SectionShell>
      ) : null}
      <div className="patient-record-workspace__journey">
        {summary !== null && summary.journey !== null && !selectedVisitMissing &&
          (selectedAppointmentId === null || (selectedIsTodayFocus && summary.journeyBasis === 'today')) ? (<SectionShell
            eyebrow="Journey"
            title="Current journey"
            sub={
              summary.journeyBasis === 'today'
                ? "Today's visit"
                : 'Most recent visit'
            }
            testId="patient360-journey"
            embedded
          >
            <ol className="flex flex-wrap gap-2" data-testid="patient360-journey-strip">
              {/* P1 -- MONOTONICITY: the strip may never claim an ordering
                  its own truth does not support. The clamp demotes an
                  unsupported claim to the owner's OWN "evidenced" word; it
                  invents no state and drops no evidence (the allowed
                  inversions -- pre-consult intake -- stay allowed). */}
              {clampJourneyLadderMonotonic(summary.journey).map((seg) => (
                <li
                  key={seg.key}
                  className="flex items-center gap-1"
                  data-testid={`patient360-journey-${seg.key}`}
                  data-state={seg.state}
                >
                  <span className="text-xs text-muted-foreground">
                    {JOURNEY_SEGMENT_LABELS[seg.key]}
                  </span>
                  <AcBadge tone={JOURNEY_TONE[seg.state]}>
                    {JOURNEY_STATE_LABELS[seg.state]}
                  </AcBadge>
                </li>
              ))}
            </ol>
          </SectionShell>) : null}
        {selectedAppointmentId === null ? <AcLink to={patientRecordHref(patient.patientId) + '/timeline'}>View journey →</AcLink> : null}
      </div>
      <SegmentedControl label="Record workspace" options={[
        {key:'overview',label:'Overview',pressed:workspace === 'overview'},
        {key:'related',label:'Related',pressed:workspace === 'related'},
        {key:'details',label:'Record details',pressed:workspace === 'details'},
      ]} onSelect={key => setWorkspace(key as 'overview' | 'related' | 'details')} />
      {summaryError !== null && summary === null ? <ErrorState title={OPERATIONAL_SUMMARY_UNAVAILABLE_COPY} testId="patient360-summary-unavailable" /> : null}
      {summary === null && summaryLoading ? <RegionSkeleton label="Loading operational record..." testId="patient360-summary-loading" /> : null}
      <div hidden={workspace !== 'overview'} className="patient-record-workspace__overview">
        <div className="grid gap-3 patient-record-workspace__overview-main">
          {audience === 'receptionist' || summary?.treatmentCheckout !== undefined ? (<SectionContainer
              title="Treatment and checkout"
              count={summary?.treatmentCheckout?.recommendedItemCount ?? operationalTreatments?.rows.length ?? 0}
              scope="Treatment decisions, completion and visit links"
              testId="patient360-treatment-detail"
              defaultOpen
            >
            {/* -- SECTION 6b: read-only treatment/checkout summary (TREATMENT-
                 ESTIMATE-ACCEPTANCE spine): counts + whole-INR sums ONLY -- no
                 procedure names, no clinical narrative, and the account line
                 stays the honest balance-not-available copy. Patient 360 is a
                 READ CONSUMER of this spine; the writers live on the doctor
                 workspace / discount routes. Optional wire field: absent =
                 older payload, panel simply does not render. */}
            {summary !== null && summary.treatmentCheckout !== undefined ? (
              <SectionShell
                eyebrow="Treatment"
                title="Treatment and checkout"
                testId="patient360-treatment"
                embedded
              >
                {summary.treatmentCheckout.availability === 'unavailable' ? (
                  <p className="text-sm" data-testid="patient360-treatment-state">
                    {TREATMENT_SUMMARY_UNAVAILABLE_COPY}
                  </p>
                ) : summary.treatmentCheckout.recommendedItemCount === 0 ? (
                  <p className="text-sm" data-testid="patient360-treatment-state">
                    {TREATMENT_NO_ITEMS_COPY}
                  </p>
                ) : (
                  <div data-testid="patient360-treatment-state">
                    <p className="text-sm" data-testid="patient360-treatment-conversion">
                      Treatment status:{' '}
                      {conversionStateLabel(
                        summary.treatmentCheckout.commercialConversionState,
                      )}
                    </p>
                    {/* PATIENT-360-V1 (FR-3, fields-not-sentences): the decision
                        counts as a labeled table -- same wire, same numbers. */}
                    <table className="table ac-table" data-testid="patient360-treatment-counts">
                      <thead>
                        <tr>
                          <th>Recommended</th>
                          <th>Accepted</th>
                          <th>Declined</th>
                          <th>Deferred</th>
                          <th>Undecided</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{summary.treatmentCheckout.recommendedItemCount}</td>
                          <td>{summary.treatmentCheckout.acceptedItemCount}</td>
                          <td>{summary.treatmentCheckout.declinedItemCount}</td>
                          <td>{summary.treatmentCheckout.deferredItemCount}</td>
                          <td>{summary.treatmentCheckout.undecidedItemCount}</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-sm" data-testid="patient360-treatment-estimate">
                      Accepted estimate:{' '}
                      {summary.treatmentCheckout.acceptedEstimateInr !== null
                        ? formatPaymentAmount(
                            summary.treatmentCheckout.acceptedEstimateInr * 100,
                            'INR',
                          )
                        : summary.treatmentCheckout.acceptedItemCount > 0
                          ? TREATMENT_PRICE_UNAVAILABLE_COPY
                          : 'No accepted items'}
                      {summary.treatmentCheckout.unknownPriceItemCount > 0
                        ? ` (${summary.treatmentCheckout.unknownPriceItemCount} item${
                            summary.treatmentCheckout.unknownPriceItemCount === 1
                              ? ''
                              : 's'
                          } without a price)`
                        : ''}
                    </p>
                    <p className="text-sm" data-testid="patient360-treatment-discount">
                      {TREATMENT_DISCOUNT_STATE_LABELS[
                        summary.treatmentCheckout.discount.state
                      ] ?? summary.treatmentCheckout.discount.state}
                      {summary.treatmentCheckout.discount.state === 'applied' &&
                      summary.treatmentCheckout.discount.discountAmountInrTotal !==
                        null &&
                      summary.treatmentCheckout.discount.finalAcceptedValueInrTotal !==
                        null
                        ? `: ${formatPaymentAmount(
                            summary.treatmentCheckout.discount.discountAmountInrTotal *
                              100,
                            'INR',
                          )} off - final ${formatPaymentAmount(
                            summary.treatmentCheckout.discount
                              .finalAcceptedValueInrTotal * 100,
                            'INR',
                          )}`
                        : ''}
                    </p>
                  </div>
                )}
              </SectionShell>
            ) : null}
            {audience === 'receptionist' ? <OperationalTreatmentTable result={operationalTreatments} onVisit={id => { setSelectedOperationalVisit(id); setWorkspace('overview'); visitDetailsRef.current?.querySelector('details')?.setAttribute('open', ''); selectedVisitRef.current?.focus(); }} /> : null}
            </SectionContainer>) : null}
          <div data-testid="patient360-active-course">
          {/* -- Multi-visit treatment journey (MULTI-VISIT-TREATMENT-JOURNEY-V1,
               V6-P4): per-course aggregates over the SAME visits + payments the
               lanes above render -- visit count, completed-vs-planned progress,
               and the course-grain PLAN REMAINDER (the billing-queue netting law
               rolled up per course; an obligation roll-up, never an account
               balance). FLAG 1: the course money line MOVED to the MONEY
               region's account line, so this card now renders COUNTS and
               progress only and the remainder exists in exactly one place. A
               voided payment still drops out of collected and the remainder
               still RESTORES on the same read -- the derivation is untouched.
               Optional wire field: absent = older payload, the card does not
               render. */}
          {summary !== null && summary.treatmentJourney !== undefined ? (
            <SectionShell
              eyebrow="Treatment"
              title="Treatment journey"
              testId="patient360-journey-lane"
            >
              {summary.treatmentJourney.availability === 'unavailable' ? (
                <p className="text-sm" data-testid="patient360-journey-state">
                  {JOURNEY_UNAVAILABLE_COPY}
                </p>
              ) : summary.treatmentJourney.courses.length === 0 ? (
                <p className="text-sm" data-testid="patient360-journey-state">
                  {JOURNEY_NO_COURSES_COPY}
                </p>
              ) : (
                <div data-testid="patient360-journey-state">
                  {summary.treatmentJourney.courses.map((course) => (
                    <div
                      key={course.courseId}
                      data-testid={`patient360-journey-course-${course.courseId}`}
                    >
                      {/* PATIENT-360-V1 (D-360d): the counts ladder over THE
                          SAME lane -- honest null renders nothing. */}
                      <JourneyStageLadder
                        ladder={deriveCountsStageLadder(
                          course.acceptedItemCount,
                          course.completedItemCount,
                        )}
                        courseLabel={`Journey #${course.courseId} - ${course.visitCount} visit${course.visitCount === 1 ? '' : 's'}`}
                        testId={`patient360-journey-ladder-${course.courseId}`}
                      />
                      <p className="text-sm">
                        {course.completedItemCount !== null &&
                        course.remainingPlannedCount !== null
                          ? `${course.completedItemCount} of ${course.acceptedItemCount} accepted items completed, ${course.remainingPlannedCount} still planned`
                          : `Journey #${course.courseId} - ${course.visitCount} visit${course.visitCount === 1 ? '' : 's'} - ${JOURNEY_COMPLETION_UNAVAILABLE_COPY}`}
                      </p>
                      {/* FLAG 1 lineage: this card still never speaks money.
                          The course ledger cells live in the MONEY block, so
                          the remainder renders exactly ONCE on the page. */}
                    </div>
                  ))}
                </div>
              )}
            </SectionShell>
          ) : null}
        </div>
          {summary !== null ? (<SectionShell
            eyebrow="History"
            title="Activity"
            testId="patient360-visits"
            /* CONTRACT s.8 + s.8a: the header carries the label AND its
               count -- "never expand to find out if it's empty", and an
               inline list states its cap where the label is, not after the
               rows. The DOOR stays in the footer beside the list, because a
               door is an action (s.5), not a statement of the cap. */
            sub={
              activityHeaderLine !== null ? (
                <span data-testid="patient360-visits-cap">
                  {activityHeaderLine}
                </span>
              ) : undefined
            }
          >
            {/* P3 -- the inline list is CAPPED and says so; the whole
                history opens in the shared DrawerShell. The page's height is
                a function of the CAP, never of this patient's tenure. */}
            <DataTable label="Related visits" rows={recentVisitRows} rowKey={v => v.appointmentId} columns={[
              { key: 'visit', header: 'Visit', render: v => <AcLink onClick={() => { setSelectedOperationalVisit(v.appointmentId); visitDetailsRef.current?.querySelector('details')?.setAttribute('open', ''); }}>{formatIstDate(v.scheduledAt)} · #{v.appointmentId}</AcLink> },
              { key: 'clinic', header: 'Clinic / doctor', render: v => <div className="ac-cell-stack">{v.clinicLabel ?? 'Clinic unavailable'}<span>{v.doctorName ?? 'Doctor unavailable'}</span></div> },
              { key: 'status', header: 'Status', render: v => appointmentStatusLabel(v.status) },
              { key: 'payment', header: 'Payment', render: v => v.paymentRecorded ? 'Recorded' : 'None recorded' },
            ]} />
            <CappedHistoryFooter
              capLine={activityCapped.capLine}
              doorLabel={fullHistoryDoorLabel(activityCapped.total, 'visits')}
              onOpen={() => setFullHistory('visits')}
              testId="patient360-visits"
              capInHeader
            />
          </SectionShell>) : null}
          <div ref={visitDetailsRef}>
            <SectionContainer title="Visit details" count={activityCapped.total} stat="Recorded activity" testId="patient360-visit-details">
              {summary !== null ? <PatientActivityLane audience={audience} entries={activityCapped.shown} testId="patient360-activity" /> : null}
              {summary !== null ? (<SectionShell
            eyebrow="Now"
            title={summary.todayFocus ? `Today · #${summary.todayFocus.appointmentId}` : 'Today'}
            testId="patient360-today"
            sub={
              summary.todayFocus !== null
                ? 'The current operational position of this patient.'
                : undefined
            }
          >
            {summary.todayAppointments.length === 0 &&
            currentAppointmentRows.length === 0 ? (
              <EmptyState title={NO_APPOINTMENT_TODAY_COPY} />
            ) : (
              <div className="grid gap-3">
                {summary.todayFocus !== null ? (
                  <TodayAppointmentCard
                    appointment={summary.todayFocus}
                    nowMs={nowMs}
                    focus
                  />
                ) : null}
                {currentAppointmentRows.length > 0 ? (
                  <div data-testid="patient360-appointments-related">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Other and upcoming appointments
                    </p>
                    <PatientAppointmentsTable
                      rows={currentAppointmentRows}
                      testId="patient360-appointments-table"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </SectionShell>) : null}
              {selectedIsTodayFocus ? <><p>{actionReason}</p><p>{patientNextAction?.reason}</p></> : null}
              {selectedOperationalVisit !== null ? (() => {
                const visit = (summary ? patientSummaryVisits(summary) : []).find(v => v.appointmentId === selectedOperationalVisit);
                return visit ? <div tabIndex={-1} ref={selectedVisitRef}><TodayAppointmentCard appointment={visit} nowMs={nowMs} focus={false} /></div> : <EmptyState title="Visit details are unavailable in the loaded history." />;
              })() : null}
            </SectionContainer>
          </div>
        </div>
        <div className="grid gap-3 patient-record-workspace__overview-sidebar" data-testid="patient360-key-facts">
          <HighlightsPanel facts={highlights.filter(f => f.key !== 'today' && f.key !== 'treatment' && (selectedIsTodayFocus || !['visit', 'doctor', 'intake'].includes(f.key)))} label="Patient key facts" testId="patient360-highlights" />
          <SectionShell title="Related records"><div className="ac-cell-stack">
            <AcLink onClick={() => setWorkspace('related')}>Billing</AcLink>
            <AcLink onClick={() => { setWorkspace('overview'); visitDetailsRef.current?.querySelector('details')?.setAttribute('open', ''); }}>Visit details</AcLink>
            <AcLink onClick={() => setWorkspace('details')}>Identity, contacts and consent</AcLink>
          </div></SectionShell>
          {summary !== null ? (<SectionShell eyebrow="Continuity" title="Next visit" testId="patient360-followup">
            <FieldGrid
              testId="patient360-next-visit-band"
              fields={[
                {
                  label: 'Booked',
                  value:
                    summary.upcomingAppointments[0] !== undefined ? (
                      <span data-testid="patient360-next-visit-booked">
                        {formatTimestampIst(
                          summary.upcomingAppointments[0].scheduledAt,
                        )}
                        {summary.upcomingAppointments[0].doctorName !== null
                          ? ` - with ${summary.upcomingAppointments[0].doctorName}`
                          : ''}
                      </span>
                    ) : (summary.nextVisitIntent ?? null) !== null ? (
                      // D-360e: no booking, a structured D1 intent exists --
                      // the honest to-schedule state (never "Next visit set").
                      <span data-testid="patient360-next-visit-intent">
                        {nextVisitIntentLine(summary.nextVisitIntent!)}
                      </span>
                    ) : (
                      <span data-testid="patient360-next-visit-none">
                        {NEXT_VISIT_NONE_COPY}
                      </span>
                    ),
                },
              ]}
            />
            <p className="text-sm" data-testid="patient360-followup-state">
              {FOLLOW_UP_STATE_LABELS[summary.followUp.state]}
              {summary.followUp.state === 'scheduled' &&
              summary.followUp.scheduledFor !== null
                ? ` - ${formatTimestampIst(summary.followUp.scheduledFor)}`
                : ''}
            </p>
          </SectionShell>) : null}
        </div>
      </div>
      <div hidden={workspace === 'details'} className="patient-record-workspace__money">
        {typeof moneySection === 'function' ? moneySection(workspace === 'overview') : moneySection}
      </div>
      <div hidden={workspace !== 'related'}>{arrivalHistory}</div>
      <div hidden={workspace !== 'related'} className="grid gap-3 patient-record-workspace__billing">
        <h2>Billing</h2>{documentsSection}{summary !== null ? (<SectionContainer
              title="Payment evidence"
              count={paymentsCapped.total}
              scope="Recorded payments and the immutable void history"
              /* A VOIDED payment is a CORRECTION to money, and #273 / FND-01
                 binds it to stay VISIBLE in history -- so this section opens
                 itself whenever the patient carries one. P4 collapses what the
                 five-second answer does not need; a money correction is not
                 that. The clean case (no void) stays collapsed. */
              defaultOpen={voidedPayments.length > 0}
              testId="patient360-payments-detail"
            >
            {/* -- SECTION 6: payment evidence --

                 PATIENT-360-FOUR-FLAG-CORRECTIVE-V1 FLAG 1 lineage: the
                 remainder renders EXACTLY ONCE on the page -- since the
                 PATIENT-360-V1 MATCH it renders as the money block's labeled
                 course cells (above), so THIS region is the evidence-list home
                 (state + active payments + void history) and its sub keeps the
                 honest balance posture.

                 FLAG 4 (binding): the money SUMMARY stays its own region. It is
                 never folded into the activity strip -- current-state vs history
                 separation and per-region role fencing depend on it. */}
            {summary !== null ? (
              <SectionShell
                eyebrow="Money"
                title="Payment evidence"
                testId="patient360-payments"
                embedded
              >
                <p className="text-sm text-muted-foreground">{PAYMENT_BALANCE_COPY}</p>
                {/* The ACTIVE collection state stays this region's own fact and
                    keeps the Patient 360 payment-evidence vocabulary verbatim
                    (PAYMENT_STATE_LABELS). StatusChip's payment canon is a
                    DIFFERENT vocabulary, so adopting it here would MOVE the
                    status wording -- which this package forbids. */}
                <p className="text-sm" data-testid="patient360-payment-state">
                  {PAYMENT_STATE_LABELS[summary.payments.state]}
                </p>
                {/* The ACTIVE collection lane, now a condensed table (D3) instead
                    of a text list. Rows, order, and vocabulary are unchanged. */}
                <div className="mt-2">
                  <PatientPaymentsTable
                    rows={paymentsCapped.shown}
                    testId="patient360-payment-list"
                  />
                  <CappedHistoryFooter
                    capLine={paymentsCapped.capLine}
                    doorLabel={fullHistoryDoorLabel(
                      paymentsCapped.total,
                      'payments',
                    )}
                    onOpen={() => setFullHistory('payments')}
                    testId="patient360-payments"
                  />
                </div>
                {/* Current-vs-history split (#273 / FND-01) -- UNMOVED. The ACTIVE
                    collection lane above stays non-voided-only (its state + the
                    commercial conversion derivation are unchanged); the VOIDED
                    rows below are the immutable corrected history, marked VOIDED,
                    and NEVER count as active collection. This banked structure --
                    and its supervisor-tier fencing of the void reason/actor --
                    keeps its home here; folding the money lane into the activity
                    strip belongs to the timeline-unification carrier (V7-P4 /
                    gap.r-40) and is a named deferral in the run record. */}
                {voidedPayments.length > 0 ? (
                  <div className="mt-3" data-testid="patient360-payment-history">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Payment history
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {PAYMENT_HISTORY_SUB_COPY}
                    </p>
                    <ul
                      className="ac-payment-history"
                      data-testid="patient360-voided-list"
                    >
                      {voidedPayments.map((v) => (
                        <VoidedPaymentRow key={v.paymentId} row={v} />
                      ))}
                    </ul>
                  </div>
                ) : paymentHistoryUnavailable ? (
                  <p
                    className="mt-3 text-sm text-muted-foreground"
                    data-testid="patient360-payment-history-unavailable"
                  >
                    {PAYMENT_HISTORY_UNAVAILABLE_COPY}
                  </p>
                ) : null}
              </SectionShell>
            ) : null}
            </SectionContainer>) : null}
      </div>
      <div hidden={workspace !== 'details'} className="grid gap-3 patient-record-workspace__details" data-testid="patient360-record-detail">
        <div
          className="flex flex-wrap items-center gap-2 patient-record-workspace__details-aliases"
          data-testid="patient360-aliases"
        >
          <span className="text-xs text-muted-foreground">External IDs</span>
          {aliases.length === 0 ? (
            <span
              className="text-sm text-muted-foreground"
              data-testid="patient360-no-external-mr"
            >
              {NO_EXTERNAL_MR_COPY}
            </span>
          ) : (
            aliases.map((a) => (
              <AcBadge
                key={`${a.system}:${a.value}`}
                tone="neutral"
                testId="patient360-alias"
                title={
                  a.external
                    ? `External identifier (${a.label}); not the AdoraCare patient number`
                    : a.label
                }
              >
                {a.label}: <span className="font-mono">{a.value}</span>
                {a.external ? ' (external)' : ''}
              </AcBadge>
            ))
          )}
        </div>

        <div className="patient-record-workspace__details-main">
        <SectionContainer
          title="More record details"
          count={
            record.identifiers.length +
            record.contactPoints.length +
            record.relatedPersons.length
          }
          scope="Identity, external records, identifiers, contacts, guardians"
          testId="patient360-secondary"
        >
          <div className="grid gap-3">
        {/* -- Identity details (F5 / #278): the secondary identity facts moved
             out of the header so the three-second set dominates above the
             fold. Still keyboard-reachable + SSR-rendered here. -- */}
        <SectionShell
          eyebrow="Record"
          title="Identity details"
          testId="patient360-identity-details"
        >
          <FieldGrid
            label="Secondary identity facts"
            fields={[
              { label: 'Date of birth', value: patient.dob, missingLabel: '—' },
              { label: 'City', value: patient.city, missingLabel: '—' },
              {
                // F-3 (timezone corrective): TRUE clinic-local date via the
                // clinic's IANA timezone (the SAME formatter Quick View uses),
                // never the UTC `.slice(0,10)` off-by-one.
                label: 'Registered on',
                value:
                  formatClinicLocalDate(
                    patient.createdAt,
                    patient.registeredClinicTimezone,
                  ) || null,
                missingLabel: '—',
              },
            ]}
          />
        </SectionShell>

        {/* -- SECTION 8: EHNote coexistence honesty -- */}
        <SectionShell
          eyebrow="External"
          title="External records (EHNote)"
          testId="patient360-external"
        >
          <p className="text-sm" data-testid="patient360-external-state">
            {EXTERNAL_RECORD_COPY}
          </p>
          <p className="text-sm text-muted-foreground">{EXTERNAL_STATUS_COPY}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {EXTERNAL_PROVENANCE_COPY}
            {aliases.some((a) => a.external)
              ? ' External identifiers on file are shown as aliases in the header.'
              : ''}
          </p>
        </SectionShell>

        {/* -- Progressive disclosure: the record detail sections. D4: each
             list now carries its OWN designed empty state (03 P11) instead
             of a bare muted paragraph -- the copy itself is unchanged. -- */}
        <SectionShell eyebrow="Record" title="Identifiers" testId="patient360-identifiers">
          <RecordDetailList
            items={record.identifiers}
            emptyTitle="No linked external identifiers."
            emptyTestId="patient360-identifiers-empty"
            itemKey={(id) => `${id.system}:${id.value}`}
            renderItem={(id) => (
              <>
                <span className="text-muted-foreground">{id.system}:</span>{' '}
                <span className="font-mono text-xs">{id.value}</span>
              </>
            )}
          />
        </SectionShell>

        <SectionShell eyebrow="Record" title="Contact points" testId="patient360-contacts">
          {record.contactPoints.length === 0 ? (
            <EmptyState
              title="No contact points on file."
              testId="patient360-contacts-empty"
            />
          ) : (
            <ul className="space-y-2">
              {record.contactPoints.map((cp) => (
                <li key={cp.id} data-testid="patient-contact-point">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {contactPointTypeLabel(cp.type)}:
                    </span>
                    {cp.isPrimary ? (
                      <span className="text-xs text-muted-foreground">primary</span>
                    ) : null}
                    {cp.doNotContact ? (
                      <span className="text-xs font-medium text-destructive">
                        do not contact
                      </span>
                    ) : null}
                  </div>
                  {/* F6 (CONTACT-POINT LAYOUT): the masked-phone row (mask +
                      "Phone on file" + context selector + Reveal) adopts the
                      existing --v2 flex layout via `polished`, fixing the
                      run-on spacing with a shared primitive (no new CSS). */}
                  <LeadContactRevealControl
                    availability={contactPointAvailability(cp.hasValue)}
                    polished
                    reveal={(purpose) =>
                      revealContact(patient.patientId, cp.id, purpose)
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </SectionShell>

        <SectionShell
          eyebrow="Record"
          title="Guardians & related persons"
          testId="patient360-related"
        >
          <RecordDetailList
            items={record.relatedPersons}
            emptyTitle="None recorded."
            emptyTestId="patient360-related-empty"
            itemKey={(rp, i) => `${rp.relationship}:${i}`}
            renderItem={(rp) => (
              <>
                {rp.relatedPatientId !== null ? (
                  <Link
                    to={patientRecordHref(rp.relatedPatientId)}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {rp.name ?? 'Linked patient'}
                  </Link>
                ) : (
                  <span className="font-medium">{rp.name ?? '-'}</span>
                )}{' '}
                <span className="text-muted-foreground">
                  ({rp.relationship}
                  {rp.isPrimaryGuardian ? ', primary guardian' : ''})
                </span>
              </>
            )}
          />
        </SectionShell>

        {/* PATIENT-360-V1: the consent records PROMOTED to the landing
            "Consent status" section above (plain labels); this disclosure
            keeps identity / external / identifiers / contacts / guardians. */}
          </div>
        </SectionContainer>
        </div>
        <div className="grid gap-3 patient-record-workspace__details-sidebar">
        <SectionShell eyebrow="Consent" title="Consent status" testId="patient360-consents">
            {/* P5 -- missing consent is AMBER: attention, the workflow
                continues. It becomes red (and a real blocker) only when
                Deepak/Partha rule that treatment or payment cannot proceed
                without recorded consent -- a policy act, never a visual one.
                P6 -- the chip always carries the WORDS of its state, so
                colour is never the only channel. */}
            <p className="mb-2">
              <span
                data-testid="patient360-consent-state"
                data-tone={consentStatus.tone}
              >
                <AcBadge tone={consentStatus.tone}>
                  {consentStatus.label}
                </AcBadge>
              </span>
            </p>
            <RecordDetailList
              items={record.consents}
              emptyTitle={CONSENTS_NONE_COPY}
              emptyTestId="patient360-consents-empty"
              itemKey={(c, i) => `${c.consentType}:${i}`}
              renderItem={(c) => (
                <>
                  <span className="font-medium">{consentTypeLabel(c.consentType)}</span>{' '}
                  <span className="text-muted-foreground">
                    granted by {c.grantedBy} ({c.channel}, {c.grantedAt.slice(0, 10)})
                  </span>
                </>
              )}
            />
          </SectionShell>
          {summary?.todayFocus ? <SectionContainer title={`Today's visit · #${summary.todayFocus.appointmentId}`} count={1}>
            {workspace === 'details' ? <TodayAppointmentCard appointment={summary.todayFocus} nowMs={nowMs} focus={false} /> : null}
          </SectionContainer> : null}
          <SectionShell title="Related records"><div className="ac-cell-stack">
            <AcLink onClick={() => setWorkspace('related')}>Billing</AcLink>
            <AcLink onClick={() => { setWorkspace('overview'); visitDetailsRef.current?.querySelector('details')?.setAttribute('open', ''); }}>Visit details</AcLink>
          </div></SectionShell>
        </div>
      </div>
      {fullHistory === 'visits' ? (<DrawerShell
            eyebrow="History"
            title={`All visits (${activityCapped.total})`}
            onClose={() => setFullHistory(null)}
            testId="patient360-visits-drawer"
          >
            <PatientActivityLane
              audience={audience}
              entries={activityEntries}
              testId="patient360-activity-full"
            />
          </DrawerShell>) : null}
      {fullHistory === 'payments' && summary !== null ? (<DrawerShell
            eyebrow="Money"
            title={`All payments (${paymentsCapped.total})`}
            onClose={() => setFullHistory(null)}
            testId="patient360-payments-drawer"
          >
            <PatientPaymentsTable
              rows={summary.payments.items}
              testId="patient360-payment-list-full"
            />
          </DrawerShell>) : null}
    </section>
  );
}

/**
 * FRONT-DESK-MONEY-AND-DOCUMENTS-V1 (PR2) -- the Patient 360 print door's
 * host state. The door is on the money row; the SWAP is the page's, because
 * a print sheet is chrome-free only when it replaces the page (the #442
 * idiom already in this repo on BillingEntryPage). Every state below is
 * presentation over an ALREADY-COMMITTED document: the reads are the
 * EXISTING GETs, no bill or payment is minted, finalized or amended by this
 * door, and nothing here writes (the print-never-repeats-money law,
 * R-G / FS-29).
 *
 * L-17(b) GENERALISED THE PAYLOAD, NOT THE LAW. The door now opens TWO kinds
 * of committed document -- the itemized bill and the payment receipt -- so
 * the type carries a document union and the names say "print door" rather
 * than "bill print". The generation / cancellation rule below is BYTE-
 * UNCHANGED: a second state machine for receipts would be the same law told
 * twice, and the second telling is where they drift apart.
 */
export type PatientPrintDocument =
  | { readonly kind: 'bill'; readonly bill: PatientBillWireMirror }
  | { readonly kind: 'receipt'; readonly payment: PatientPaymentRow };

export type PatientPrintDoorState =
  | { readonly kind: 'idle'; readonly seq: number }
  | { readonly kind: 'loading'; readonly seq: number; readonly clinicId: number }
  | {
      readonly kind: 'error';
      readonly seq: number;
      readonly reason: PatientPrintDoorFailure;
    }
  | {
      readonly kind: 'ready';
      readonly seq: number;
      readonly document: PatientPrintDocument;
      readonly clinicId: number;
    };

/** Events the door's state machine accepts. `seq` on the two RESPONSE events
 *  is the generation of the request that produced them -- never the current
 *  one. */
export type PatientPrintDoorEvent =
  | { readonly kind: 'open'; readonly clinicId: number }
  /** The operator pressed Back. */
  | { readonly kind: 'dismiss' }
  /** The route moved to a different patient. */
  | { readonly kind: 'patient-changed' }
  | {
      readonly kind: 'loaded';
      readonly seq: number;
      readonly document: PatientPrintDocument;
      readonly clinicId: number;
    }
  | {
      readonly kind: 'failed';
      readonly seq: number;
      readonly reason: PatientPrintDoorFailure;
    };

export const PATIENT_PRINT_DOOR_INITIAL: PatientPrintDoorState = {
  kind: 'idle',
  seq: 0,
};

/**
 * THE CANCELLATION LAW, pure and pinned.
 *
 * A print door opens an already-committed money document, and the read behind
 * it is asynchronous. Without this, the response that was in flight when the
 * operator pressed Back still landed -- and re-opened a document she had
 * dismissed, over whatever she was doing next. On a money document that is
 * worse than a slow load.
 *
 * So: `open`, `dismiss` and `patient-changed` all ADVANCE the generation, and
 * a `loaded` / `failed` response is applied ONLY when its own generation is
 * still the current one. A superseded response is DROPPED SILENTLY -- it
 * returns the state UNCHANGED, never an error state, because a request the
 * operator abandoned did not fail.
 *
 * Pure by construction so the law is testable without a browser (the repo's
 * `nextLinkedAppointment` idiom in billing-entry-view.ts).
 */
export function nextPrintDoorState(
  current: PatientPrintDoorState,
  event: PatientPrintDoorEvent,
): PatientPrintDoorState {
  switch (event.kind) {
    case 'open':
      return { kind: 'loading', seq: current.seq + 1, clinicId: event.clinicId };
    case 'dismiss':
    case 'patient-changed':
      // Advancing the generation is what CANCELS: any response still in
      // flight now names a superseded request and will be dropped below.
      return { kind: 'idle', seq: current.seq + 1 };
    case 'loaded':
      if (event.seq !== current.seq) return current;
      return {
        kind: 'ready',
        seq: current.seq,
        document: event.document,
        clinicId: event.clinicId,
      };
    case 'failed':
      if (event.seq !== current.seq) return current;
      return { kind: 'error', seq: current.seq, reason: event.reason };
  }
}

/** No bill exists for the visit yet. HONEST ABSENCE, never a mint: raising a
 *  bill is a WRITE and a print door never performs one. */
/** The door's return label -- it NAMES the surface it returns to (the rail's
 *  backLabel contract). */
export const PATIENT_PRINT_DOOR_BACK_LABEL = 'Back to the patient record';

/**
 * THE TWO UNHAPPY STATES, AND WHY THEY MUST NOT SHARE A HEADING.
 *
 * `no-bill` means the read SUCCEEDED and the answer was "nothing was billed".
 * `read-failed` means we do not KNOW what was billed. They send the operator
 * to different actions -- accept, versus retry or escalate -- so a single
 * heading covering both ("Nothing to print yet") is TRUE for the first and
 * FALSE for the second: it reports a failure as an absence, and a screen that
 * does that teaches her to distrust every empty state she sees.
 *
 * ONE home for both, so a test and a frame can assert that the two headings
 * DIFFER and that neither describes the other's condition.
 */
export type PatientPrintDoorFailure =
  | 'no-bill'
  | 'read-failed'
  | 'receipt-read-failed';

export const PATIENT_PRINT_DOOR_FAILURE_COPY: Readonly<
  Record<PatientPrintDoorFailure, { readonly title: string; readonly detail: string }>
> = {
  'no-bill': {
    title: 'No bill has been raised for this visit',
    detail:
      'The visit was read and nothing has been billed on it yet. Raise the bill on Billing entry, then print it.',
  },
  'read-failed': {
    title: "Could not read this visit's bills",
    detail:
      'The bill list did not load, so what was billed on this visit is unknown. Try again; if it keeps failing, print from Billing entry or escalate.',
  },
  // L-17(b): the receipt lane's OWN unknown-state heading. It never borrows
  // the bill lane's words, and there is deliberately NO 'no-receipt' member:
  // the receipt door only ever opens over a row the list already served, so
  // "nothing was received" is a state this door cannot reach.
  'receipt-read-failed': {
    title: 'Could not open this receipt',
    detail:
      'The receipt did not load, so what was received is unknown here. Try again; if it keeps failing, open it from Billing entry or escalate.',
  },
};

export interface PatientRecordPageProps {
  readonly patientIdOverride?: string;
  readonly selectedAppointmentId?: number;
  readonly embedded?: boolean;
}

function PatientRecordBookingHost({ enabled, children }: { enabled: boolean; children: ReactNode }): JSX.Element {
  const inherited = useContext(WorkspaceSurfaceContext);
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const onOpenChange = useCallback((value: boolean) => setOpen(value), []);
  useEffect(() => {
    // Seed the existing docked ModalShell after its hidden workspace aside is
    // committed; unrelated nested dialogs are not this host's focus target.
    if (open) target?.querySelector<HTMLElement>('.ac-workspace-surface > .ac-modal--docked')?.focus();
  }, [open, target]);
  if (!enabled || inherited !== null) return <>{children}</>;
  return <WorkspaceSurfaceContext.Provider value={{ target, onOpenChange }}>
    <div className="ac-workspace-panels patient-record-booking-host">
      <MasterDetail single={!open} testId="patient-record-booking-workspace">
        <div className="ac-workspace-panels__list">{children}</div>
        <DetailAside label="Book appointment"><div ref={setTarget} /></DetailAside>
      </MasterDetail>
    </div>
  </WorkspaceSurfaceContext.Provider>;
}

export default function PatientRecordPage({ patientIdOverride, selectedAppointmentId, embedded = false }: PatientRecordPageProps = {}): JSX.Element {
  const { patientId: routePatientId } = useParams<{ patientId: string }>();
  const patientId = patientIdOverride ?? routePatientId;
  // The print sheet swaps out the content; retain the selected workspace in
  // this host, keyed to the patient so navigation never carries it to another record.
  const [recordWorkspace, setRecordWorkspace] = useState<{ patientId: string | undefined; workspace: RecordWorkspace }>({ patientId, workspace: 'overview' });
  // L-18: the URL is the WHOLE carrier (the primitive's law) -- a reload, a
  // shared link and the browser's own Back all agree, and nothing is stored.
  const [searchParams] = useSearchParams();
  const recordOrigin = parseDeskRecordOrigin(searchParams.get('from'));
  const meCtx = useDashboardMeContext();
  const clinicLens = useClinicLens();
  const [billPrint, setBillPrint] = useState<PatientPrintDoorState>(
    PATIENT_PRINT_DOOR_INITIAL,
  );
  // The generation the LOADER reads when a response lands. It mirrors
  // billPrint.seq, in a ref, because a promise closure must compare against
  // the CURRENT generation rather than the one captured when it started.
  const billPrintSeq = useRef(PATIENT_PRINT_DOOR_INITIAL.seq);
  const dispatchBillPrint = useCallback((event: PatientPrintDoorEvent) => {
    setBillPrint((current) => {
      const next = nextPrintDoorState(current, event);
      billPrintSeq.current = next.seq;
      return next;
    });
  }, []);

  // A PATIENT CHANGE CANCELS TOO. Landing on another patient's record must
  // never inherit a bill read armed on the previous one.
  useEffect(() => {
    dispatchBillPrint({ kind: 'patient-changed' });
  }, [patientId, dispatchBillPrint]);
  const [operational, setOperational] = useState<{ patientId: string; role: string; result: import('../types').OperationalTreatmentsResult } | null>(null);
  const [record, setRecord] = useState<PatientRecordResponse | null>(null);
  const [summary, setSummary] = useState<Patient360SummaryResponse | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // D4: the operational lane's OWN in-flight flag, so its regions can render
  // designed skeletons instead of silently rendering nothing. It tracks the
  // SAME existing summary read -- this package adds no request.
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!patientId || meCtx?.me?.role !== 'receptionist') return;
    let cancelled = false;
    const id = patientId;
    fetchPatientOperationalTreatments(id).then(result => {
      if (!cancelled) setOperational({ patientId: id, role: 'receptionist', result });
    }).catch(() => {
      if (!cancelled) setOperational({ patientId: id, role: 'receptionist', result: { availability: 'unavailable', rows: [], truncated: false } });
    });
    return () => { cancelled = true; };
  }, [patientId, meCtx?.me?.role]);

  // RECEPTION-OS-BUILD-V1 (Q-06=B): remember this lawful open in the
  // per-tab session memory (id + timestamp only; the lead pattern). The
  // record READ below is the access decision -- the memory never is.
  useEffect(() => {
    if (patientId) recordPatientOpened(patientId, Date.now());
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      setError('Missing patient id');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(undefined);
    setSummaryError(null);
    setSummaryLoading(true);

    // The two reads are independent by design: the record read is the
    // identity owner; the summary read is the operational lane. A summary
    // failure NEVER blanks the page (graceful degradation).
    fetchPatientMasterRecord(patientId)
      .then((res) => {
        if (cancelled) return;
        setRecord(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(safeErrorMessage(err, 'Failed to load patient'));
        setErrorStatus(portalErrorStatus(err));
        setLoading(false);
      });
    fetchPatient360Summary(patientId, selectedAppointmentId)
      .then((res) => {
        if (cancelled) return;
        setSummary(res);
        setSummaryLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSummaryError(
          err instanceof Error ? err.message : 'Failed to load summary',
        );
        setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, selectedAppointmentId]);

  // PR2 (LEAVES-2 / L-17): the door's loader. TWO EXISTING READS ONLY --
  // the appointment's bill list, then that bill's own detail. Newest-first
  // is the list route's own order, so [0] is the current bill.
  const openBillPrint = (row: {
    readonly appointmentId: number;
    readonly clinicId: number;
  }): void => {
    // CANCELLATION: `open` advances the generation, and THIS request carries
    // the generation it created. Every response below names that generation,
    // so `nextBillPrintState` can drop it silently once Back or a patient
    // change has superseded it.
    const seq = billPrintSeq.current + 1;
    dispatchBillPrint({ kind: 'open', clinicId: row.clinicId });
    fetchPatientBills({ appointmentId: row.appointmentId })
      .then((res) => {
        const list = readPatientBillListWire(res);
        if (list.kind !== 'ready') {
          // THE WIRE WAS NOT SERVED. Not the same thing as "no bill" -- see
          // the two copies, which never share a heading.
          dispatchBillPrint({ kind: 'failed', seq, reason: 'read-failed' });
          return;
        }
        const newest = list.bills[0];
        if (newest === undefined) {
          dispatchBillPrint({ kind: 'failed', seq, reason: 'no-bill' });
          return;
        }
        return fetchPatientBill(newest.id).then((detail) => {
          dispatchBillPrint({
            kind: 'loaded',
            seq,
            document: { kind: 'bill', bill: detail.bill },
            clinicId: row.clinicId,
          });
        });
      })
      .catch(() => {
        // A THROWN read is the same class as an unserved wire: we do not know
        // what was billed. It is NEVER reported as "no bill".
        dispatchBillPrint({ kind: 'failed', seq, reason: 'read-failed' });
      });
  };

  // L-17(b): open ONE NAMED bill from the patient's own bill list. The visit
  // door above resolves "the newest bill on this appointment"; this one is
  // handed the bill id the operator picked, so an EARLIER bill on the same
  // visit is reachable too -- the gap the newest-only door left open.
  const openBillById = (row: {
    readonly billId: number;
    readonly clinicId: number;
  }): void => {
    const seq = billPrintSeq.current + 1;
    dispatchBillPrint({ kind: 'open', clinicId: row.clinicId });
    fetchPatientBill(row.billId)
      .then((detail) => {
        dispatchBillPrint({
          kind: 'loaded',
          seq,
          document: { kind: 'bill', bill: detail.bill },
          clinicId: row.clinicId,
        });
      })
      .catch(() => {
        dispatchBillPrint({ kind: 'failed', seq, reason: 'read-failed' });
      });
  };

  // L-17(b): re-print an ALREADY-COMMITTED receipt. The row is the one the
  // list already served, so there is NO second read and no chance of the
  // printed numbers differing from the day they were issued -- the sheet
  // renders the committed patient_payments row verbatim.
  const openReceipt = (payment: PatientPaymentRow): void => {
    const seq = billPrintSeq.current + 1;
    dispatchBillPrint({ kind: 'open', clinicId: payment.clinicId });
    // B3: DO NOT PRINT THE CACHED ROW. A supervisor may have voided this
    // payment since the list loaded, and a sheet that still says "Active"
    // over a reversed payment is a document the clinic could hand out.
    //
    // CORRECTIVE 2: the refresh is BY STABLE IDENTITY, not by re-reading the
    // window the row came from. A payment inserted since the list loaded
    // sorts ahead of it and can push it out of that window entirely, and the
    // door would then report a read failure on a receipt that exists. The
    // paymentId filter rides the SAME read, so the org fence, the per-clinic
    // entry-role resolution and the patient narrowing all still apply.
    //
    // Amount, method, reference and timestamp are immutable on a committed
    // row, so the refreshed row IS the issue-day record; only STATUS moves.
    fetchPatientReceipts({
      patientId: payment.patientId,
      paymentId: payment.id,
      limit: 1,
    })
      .then((res) => {
        const fresh = res.payments.find((p) => p.id === payment.id);
        if (fresh === undefined) {
          dispatchBillPrint({ kind: 'failed', seq, reason: 'receipt-read-failed' });
          return;
        }
        dispatchBillPrint({
          kind: 'loaded',
          seq,
          document: { kind: 'receipt', payment: fresh },
          clinicId: fresh.clinicId,
        });
      })
      .catch(() => {
        dispatchBillPrint({ kind: 'failed', seq, reason: 'receipt-read-failed' });
      });
  };

  const closeBillPrint = (): void => dispatchBillPrint({ kind: 'dismiss' });

  const printClinicLabel = (clinicId: number): string => {
    const option =
      clinicLens?.options.find((o) => o.clinicId === clinicId) ?? null;
    return option !== null ? clinicOptionLabel(option) : clinicLabelOf(null);
  };

  // THE SWAP: the print rail replaces the page, so what prints carries no
  // dashboard chrome (BillingEntryPage's own idiom, reused -- this page
  // mounts NO second print system and renders NO document of its own; the
  // artifact is the EXISTING BillPrintView over the EXISTING PrintSheet).
  if (billPrint.kind === 'loading') {
    return (
      <div className="ac-print-sheet" data-testid="patient360-bill-print-loading">
        <LoadingState label="Opening the document..." lines={3} />
        <AcButton
          variant="ghost"
          onClick={closeBillPrint}
          data-testid="patient360-bill-print-back"
        >
          {PATIENT_PRINT_DOOR_BACK_LABEL}
        </AcButton>
      </div>
    );
  }
  if (billPrint.kind === 'error') {
    return (
      <div
        className="ac-print-sheet"
        data-testid="patient360-bill-print-error"
        data-bill-print-reason={billPrint.reason}
      >
        <ErrorState
          title={PATIENT_PRINT_DOOR_FAILURE_COPY[billPrint.reason].title}
          detail={PATIENT_PRINT_DOOR_FAILURE_COPY[billPrint.reason].detail}
          testId={`patient360-bill-print-${billPrint.reason}`}
        />
        <AcButton
          variant="ghost"
          onClick={closeBillPrint}
          data-testid="patient360-bill-print-back"
        >
          {PATIENT_PRINT_DOOR_BACK_LABEL}
        </AcButton>
      </div>
    );
  }
  if (billPrint.kind === 'ready') {
    // TWO REGISTERED SHEETS, NO THIRD. BillPrintView is the bill half and
    // ReceiptPrintView the payment half of the EXISTING print family; this
    // page mounts neither a second print system nor a document of its own.
    return billPrint.document.kind === 'bill' ? (
      <BillPrintView
        bill={billPrint.document.bill}
        clinicLabel={printClinicLabel(billPrint.clinicId)}
        backLabel={PATIENT_PRINT_DOOR_BACK_LABEL}
        onBack={closeBillPrint}
      />
    ) : (
      <ReceiptPrintView
        payment={billPrint.document.payment}
        clinicLabel={printClinicLabel(billPrint.clinicId)}
        backLabel={PATIENT_PRINT_DOOR_BACK_LABEL}
        onBack={closeBillPrint}
      />
    );
  }

  const visibleRecord = record?.patient.patientId === patientId ? record : null;
  const visibleSummary = summary?.patientId === patientId ? summary : null;

  return (
    <PatientRecordBookingHost key={patientId} enabled={meCtx?.me?.role === 'receptionist'}>
    <PatientRecordContent
      workspace={recordWorkspace.patientId === patientId ? recordWorkspace.workspace : 'overview'}
      onWorkspaceChange={workspace => setRecordWorkspace({ patientId, workspace })}
      key={patientId}
      loading={loading || (record !== null && visibleRecord === null)}
      error={error}
      errorStatus={errorStatus}
      arrivalHistory={patientId && meCtx?.me?.role === 'receptionist' ? <div key={`${patientId}:${JSON.stringify(meCtx.me)}`}><WalkinFollowupsPanel schedulePatientId={patientId} schedulePatientUmr={visibleRecord?.patient.umr}/><PatientArrivalHistory patientId={patientId}/></div> : null}
      record={visibleRecord}
      summary={visibleSummary}
      summaryError={summaryError}
      summaryLoading={summaryLoading}
      selectedAppointmentId={selectedAppointmentId ?? null}
      operationalTreatments={operational !== null && operational.patientId === patientId && operational.role === meCtx?.me?.role ? operational.result : null}
      // The viewer's role from SERVER truth. The route guard above this page
      // only mounts it once `me` has resolved into the Patient Master
      // audience, so this is never a stale or absent audience in the app --
      // and the primitives fail closed if it ever were.
      audience={meCtx?.me?.role ?? null}
      canOpenMergeQueue={meCtx?.me?.patientMasterMergeEnabled === true}
      nowMs={Date.now()}
      moneySection={(summaryOnly) => (
        // R-Q04: the ONE-CLICK read-only financial view, mounted by the
        // page host (it owns the id + the summary's payment evidence).
        patientId !== undefined && patientId !== '' ? (
          <PatientMoneySection
            patientId={patientId}
            summaryOnly={summaryOnly}
            onPrintBill={openBillPrint}
            courseMoney={
              visibleSummary !== null ? buildCourseMoneyLines(visibleSummary) : null
            }
            lastPayment={(() => {
              const items = visibleSummary?.payments.items ?? [];
              if (items.length === 0) return null;
              const newest = [...items].sort((a, b) =>
                a.occurredAt < b.occurredAt ? 1 : -1,
              )[0];
              return newest === undefined
                ? null
                : {
                    amountLabel: formatPaymentAmount(
                      newest.amountMinorUnits,
                      newest.currency,
                    ),
                    occurredAt: newest.occurredAt,
                  };
            })()}
          />
        ) : null
      )}
      backDoor={embedded ? null : deskRecordBackDoor(recordOrigin, patientId ?? null, searchParams)}
      documentsSection={
        // L-17(b): the document lane sits BESIDE the money lane, never inside
        // it -- money is a derivation, a document is a record, and folding one
        // into the other is how a re-print starts claiming a balance.
        patientId !== undefined && patientId !== '' ? (
          <PatientDocumentsSection
            patientId={patientId}
            onPrintBill={openBillById}
            onPrintReceipt={openReceipt}
          />
        ) : null
      }
    />
    </PatientRecordBookingHost>
  );
}
